import useMotionTrackingEntitlement, { clearMotionTrackingSession } from "../hooks/useMotionTrackingEntitlement";

function sanitizeThreeColor(value: string | null | undefined) {
  if (!value) return "#7c8cff";

  const v = value.trim();

  // reject gradients/shadows/etc
  if (
    v.includes("gradient") ||
    v.includes("rgba") ||
    v.includes("hsla") ||
    v.includes("var(") ||
    v.includes("shadow")
  ) {
    return "#7c8cff";
  }

  // allow hex
  if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(v)) {
    return v;
  }

  // ⚠️ iPad/TestFlight WebKit can destabilize on malformed rgb()
  // values during theme hydration. Convert rgb usage to safe fallback.
  if (/^rgb\(/.test(v)) {
    try {
      const match = v.match(
        /^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/
      );

      if (!match) {
        return "#7c8cff";
      }

      const r = Math.min(255, Math.max(0, Number(match[1])));
      const g = Math.min(255, Math.max(0, Number(match[2])));
      const b = Math.min(255, Math.max(0, Number(match[3])));

      if (
        !Number.isFinite(r) ||
        !Number.isFinite(g) ||
        !Number.isFinite(b)
      ) {
        return "#7c8cff";
      }

      return `rgb(${r}, ${g}, ${b})`;
    } catch {
      return "#7c8cff";
    }
  }

  return "#7c8cff";
}

let avaRuntimeAccentColor: string | null = null;

function getAvaAccentColor() {
  try {
    if (avaRuntimeAccentColor) {
      return avaRuntimeAccentColor;
    }

    const styles = getComputedStyle(document.documentElement);

    const raw =
      styles.getPropertyValue("--ava-face-accent").trim() ||
      "#7c8cff";

    return sanitizeThreeColor(raw);
  } catch {
    return "#7c8cff";
  }
}

// /home/ubuntu/avaai_dashboard/frontend/src/components/AvaFace.tsx
// Surgical mobile framing patch: keeps architecture, improves mobile close-up

import React, { useRef, useState, useEffect, useMemo, useContext , Suspense } from "react";
import { createPortal } from "react-dom";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { AVA_MESH_REGISTRY }
from "../config/avaMeshRegistry";

import {
  AVA_EXPRESSIONS,
  AvaExpressionName,
} from "../config/avaExpressions";

import { Environment, useGLTF, useTexture } from "@react-three/drei";
import { Bloom, EffectComposer } from "@react-three/postprocessing";
import * as THREE from "three";
import "../styles/AvaFace.css";
import { useCameraFaceTracking } from "../hooks/useCameraFaceTracking";
import { uploadVisionFrame } from "../services/avaVisionUploader";
import {
  setLatestVision,
  getLatestVision,
  clearLatestVision,
} from "../services/avaVisionContext";
import { ThemeContext } from "../context/ThemeContext";
import CameraRig from "./rendering/AvaCamera";
import { enhanceAvaMaterials } from "./rendering/AvaMaterialEngine";
import AvaLighting from "./rendering/AvaLighting";
import AvaVisionHUD from "./AvaVisionHUD";

const AVA_EYE_IDLE_MS = 5 * 60 * 1000;




const USE_ENVIRONMENT = false;

// AVA_ARTIST_RENDER_TEST
// Disabled by default. Build 20 and Ava's current appearance remain unchanged.
const USE_AVA_BLOOM_TEST = false;

const LENS_MATERIAL_NAME = "GLASS";
const ALSO_NO_SPECULAR = new Set<string>(["glossy material"]);

const BASE_GUNMETAL = "#cfd8e6";
const PANEL_WHITE = "#eef3fb";
const NON_LENS_METALNESS = 0.34;
const NON_LENS_ROUGHNESS = 0.48;

// 🔥 iPad/TestFlight GPU stabilizer
function isIOSWebKit() {
  try {
    const ua = navigator.userAgent || "";

    return (
      /iPad|iPhone|iPod/.test(ua) ||
      (
        navigator.platform === "MacIntel" &&
        navigator.maxTouchPoints > 1
      )
    );
  } catch {
    return false;
  }
}

const IS_IPAD_DEVICE =
  typeof navigator !== "undefined" &&
  (
    /iPad/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );


const IOS_GPU_SAFE_MODE =
  isIOSWebKit() &&
  (
    navigator.maxTouchPoints > 1 &&
    Math.max(window.innerWidth, window.innerHeight) >= 1024
  );

// =====================================================
// AVA_IPAD_NORMAL_MODEL_SAFE_GPU_V10E2
//
// Keep the established iPad GPU/resource protections,
// but do not let that safety flag choose a different Ava.
//
// Sandbox experiment:
// normal AvaModel + GPU-safe WebGL configuration.
// =====================================================
const USE_IPAD_RAW_AVA_RENDERER = false;

// =====================================================
// AVA_IPAD_GPU_VISUAL_SPLIT_V10F1
//
// GPU/resource safety remains active independently.
//
// Visual restrictions are OFF so landscape cold-start
// uses the same Ava appearance path as portrait.
// =====================================================
const IPAD_GPU_RESOURCE_SAFE =
  IOS_GPU_SAFE_MODE;

const IPAD_VISUAL_RESTRICTIONS =
  false;

// =====================================================
// AVA_BUILD23_VISUAL_RECONSTRUCTION_V1
//
// Reproduce the preferred Build-23 old-iPad appearance
// independently from hardware/GPU resource protection.
//
// GPU safety remains controlled by IPAD_GPU_RESOURCE_SAFE.
// This flag controls visual treatment only.
// =====================================================
const AVA_BUILD23_VISUAL_STYLE = true;

const MAX_EMISSIVE_IOS = 4.5;
const MAX_EMISSIVE_NORMAL = 18.0;

const FRAME_SKIP_IOS = 2;

console.log("[AVA IOS MODE]", {
  IOS_GPU_SAFE_MODE,
  IS_IPAD_DEVICE,
  isIOSWebKit: isIOSWebKit(),
  platform: navigator.platform,
  userAgent: navigator.userAgent,
  maxTouchPoints: navigator.maxTouchPoints,
  width: window.innerWidth,
  height: window.innerHeight,
});




const EMISSIVE_TARGET_HINTS = [
  "eye_emissive",
  "mouth_emissive",
  "top strip",
  "top strip circuit",
  "side_emissive",
  "horn_emissive",
  "propulsion emission",
  "emissive",
  "circuit",
  "strip",
  "glow",
];

const EYE_EMISSIVE_HINTS = ["eye", "iris", "pupil"];

const HALO_POS: [number, number, number] = [0, 0.93, 0.62];
const HALO_RADIUS = 0.155;
const HALO_TUBE = 0.010;
const HALO_AWAKE_SECONDS = 6.0;

// Small neutral pitch correction so Ava spawns looking toward center instead of slightly upward.
const AVA_NEUTRAL_HEAD_PITCH = 0.215;

function fract(x: number) {
  return x - Math.floor(x);
}

function hash11(x: number) {
  return fract(Math.sin(x * 127.1) * 43758.5453123);
}

function useViewportProfile() {
  const getProfile = () => {
    if (typeof window === "undefined") {
      return {
        initialZ: 2.1,
        farZ: 2.1,
        nearZ: 1.42,
        fov: 41,
        modelScale: 1.86,
        modelY: 0.56,
        modelZ: 0.14,
      };
    }

    const w = window.innerWidth;
    const h = window.innerHeight;

    console.log("[AVA Viewport Debug]");
    console.log({
      width: w,
      height: h,
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      maxTouchPoints: navigator.maxTouchPoints,
    });

    const isMobile = w <= 900;
    const isLandscape = w > h;

    // =====================================================
    // AVA_IPAD_ORIENTATION_SCALE_NORMALIZATION_V8_1
    //
    // Do not classify the same iPad as "tablet" in portrait
    // and "desktop" in landscape merely because its CSS width
    // crosses 1024px after rotation.
    //
    // Keep the camera distance, FOV and model scale identical
    // across iPad orientations so Ava occupies approximately
    // the same percentage of the screen.
    // =====================================================
    if (IPAD_VISUAL_RESTRICTIONS) {
      // =====================================================
      // AVA_IPAD_PORTRAIT_LANDSCAPE_VISUAL_MATCH_V9A5
      //
      // One exact visual profile for both orientations.
      //
      // Portrait is the approved reference.
      // Landscape uses the SAME camera, FOV, model scale,
      // vertical position and depth.
      // =====================================================
      return {
        initialZ: 2.02,
        farZ: 2.08,
        nearZ: 1.42,
        fov: 41,
        modelScale: 1.72,
        modelY: 0.34,
        modelZ: 0.10,
      };
    }

    // AVA_IPHONE_LANDSCAPE_UNIFIED_PROFILE
    // Match the same 932x520 boundary used by ChatDock.css.
    // Width alone cannot identify modern iPhones in landscape,
    // because their reported landscape width often exceeds 640px.
    const isIPhoneLandscape =
      isLandscape &&
      w <= 932 &&
      h <= 520;

    const isTablet =
      !isIPhoneLandscape &&
      w > 640 &&
      w <= 1024;

    const isPhone =
      !isLandscape &&
      w <= 640;

    console.log("[AVA Viewport Flags]", {
      isMobile,
      isTablet,
      isPhone,
      isLandscape,
    });


    if (isIPhoneLandscape) {
      return {
        // AVA_IPHONE_LANDSCAPE_LOWER_CLOSE_PROFILE
        // iPhone landscape is shorter than iPad landscape.
        // Bring Ava closer and lower while preserving the full head.
        initialZ: 1.70,
        farZ: 1.78,
        nearZ: 1.28,
        fov: 39,
        modelScale: 1.94,
        modelY: 0.30,
        modelZ: 0.12,
      };
    }

    if (isPhone && isLandscape) {
      return {
        initialZ: 1.78,
        farZ: 1.86,
        nearZ: 1.34,
        fov: 42,
        modelScale: 1.90,
        modelY: 0.10,
        modelZ: 0.14,
      };
    }

if (isPhone) {
      return {
        initialZ: 1.86,
        farZ: 2.10,
        nearZ: 1.52,
        fov: 42,
        modelScale: 1.58,
        modelY: 0.30,
        modelZ: 0.06,
      };
    }

    if (isTablet && isLandscape) {
      return {
        initialZ: 1.74,
        farZ: 1.8,
        nearZ: 1.3,
        fov: 39,
        modelScale: 1.78,
        modelY: 0.56,
        modelZ: 0.14,
      };
    }

    if (isTablet) {
      return {
        initialZ: 2.05,
        farZ: 2.10,
        nearZ: 1.42,
        fov: 42,
        modelScale: 1.72,
        modelY: 0.34,
        modelZ: 0.10,
      };
    }

    if (isMobile && isLandscape) {
      return {
        initialZ: 1.64,
        farZ: 1.7,
        nearZ: 1.24,
        fov: 38,
        modelScale: 1.72,
        modelY: 0.63,
        modelZ: 0.12,
      };
    }

    if (isMobile) {
      return {
        initialZ: 1.98,
        farZ: 2.04,
        nearZ: 1.4,
        fov: 40,
        modelScale: 1.84,
        modelY: 0.68,
        modelZ: 0.16,
      };
    }

    return {
      initialZ: 2.26,
      farZ: 2.26,
      nearZ: 1.52,
      fov: 38,
      modelScale: 1.82,
      modelY: 0.48,
      modelZ: 0.20,
    };
  };

  const [profile, setProfile] = useState(getProfile);

  useEffect(() => {

    const onResize = () => setProfile(getProfile());

    // Force viewport re-evaluation after login redirect
    // and Safari viewport settling.
    onResize();

    requestAnimationFrame(onResize);

    window.setTimeout(onResize, 150);

    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  return profile;
}

// CameraRig moved to rendering/AvaCamera

// ==========================================================
// AVA FACIAL MORPH MAP
// Verified from AVA_REVISED.glb calibration.
// Update only if the GLB changes.
// ==========================================================

const AVA_MORPH_NEUTRAL = 0;
const AVA_MORPH_SAD = 1;
const AVA_MORPH_SURPRISED = 2;


// ==========================================================
// AVA RUNTIME EXPRESSIONS
// ==========================================================

const AVA_EXPRESSION_NEUTRAL   = "NEUTRAL";
const AVA_EXPRESSION_SMILE     = "SMILE";
const AVA_EXPRESSION_HAPPY     = "HAPPY";
const AVA_EXPRESSION_SAD       = "SAD";
const AVA_EXPRESSION_FROWN     = "FROWN";
const AVA_EXPRESSION_SURPRISED = "SURPRISED";
const AVA_EXPRESSION_ANGRY     = "ANGRY";
const AVA_EXPRESSION_THINKING  = "THINKING";
const AVA_EXPRESSION_CONFUSED  = "CONFUSED";


function AvaModel({
  eyeOpen,
  visionActive,
  visionTrackingActive,
  visionFaceDetected,
  visionStatus,
  visionCaptureCanvasFrameRef,
  speaking,
  mouthLevel,
  look,
  awakeLevel,
  scale,
  position,
  avaEyeOpen,
  accentColor,
  expression = "NEUTRAL",
  motionLookActive = false,
}: {
  eyeOpen: boolean;
  visionActive: boolean;
  visionTrackingActive: boolean;
  visionFaceDetected: boolean;
  visionStatus: AvaVisionUserStatus;
  visionCaptureCanvasFrameRef: {
    current: () => HTMLCanvasElement | null;
  };
  speaking: boolean;
  mouthLevel: number;
  look: { x: number; y: number };
  awakeLevel: number;
  scale: number;
  position: [number, number, number];
  avaEyeOpen: boolean;
  accentColor?: string;
  expression?: string;
  motionLookActive?: boolean;
}) {
  // ThemeContext bridged via accentColor prop (R3F Canvas context isolation fix)

  const group = useRef<THREE.Group>(null);

  /*
   * Phase 4D Vision reacquisition memory.
   *
   * Ava tracks quickly while the user is visible. When the
   * user leaves frame, she eases back to center and then
   * begins a restrained search pattern.
   */
  const visionLastFaceSeenRef =
    useRef(
      typeof performance !== "undefined"
        ? performance.now()
        : Date.now()
    );

  const visionSearchBlendRef =
    useRef(0);

  // =========================================
  // AVAAIO HOLOGRAPHIC ENERGY PULSE SYSTEM
  // =========================================

  const pulseTime =
    typeof performance !== "undefined"
      ? performance.now() * 0.001
      : 0;

  const pedestalPulse =
    0.72 + Math.sin(pulseTime * 2.4) * 0.28;

  const beamPulse =
    0.10 + Math.sin(pulseTime * 4.0) * 0.05;

  const isLandscapePhone =
    typeof window !== "undefined"
      ? window.innerWidth <= 640 &&
        window.innerWidth > window.innerHeight
      : false;

  const emitterOffsetX = 0;

  const ringPulse =
    0.22 + Math.sin(pulseTime * 1.8) * 0.08;


  // ==========================================================
  // AVA MOUTH EXPRESSION SYSTEM
  // ==========================================================

  const mouthMeshRef = useRef<any>(null);

  const mouthMaterialRef = useRef<any>(null);

  const mouthMorphDictRef = useRef<any>(null);

  const mouthInfluencesRef = useRef<any>(null);

  const mouthExpressionRef =
    useRef<"NEUTRAL" | "SAD" | "SURPRISED">(
      "NEUTRAL"
    );

  // ==========================================================
  // AVA EXPRESSION ROUTER
  // ==========================================================

const lastExpressionLogRef = useRef<string>("");

  const expressionStateRef = useRef<
    | "NEUTRAL"
    | "HAPPY"
    | "SAD"
    | "SURPRISED"
    | "THINKING"
    | "CONFUSED"
    | "EXCITED"
  >("NEUTRAL");


  const haloRef = useRef<THREE.Mesh>(null);

  // Natural gaze controller:
  // Ava gently centers toward the user while responding, then eases back to idle.
  const naturalLookRef = useRef({
    yaw: 0,
    pitch: AVA_NEUTRAL_HEAD_PITCH,
    roll: 0,
  });

  // ==========================================================
  // AVA ATTENTION MEMORY
  // ==========================================================

  const lastAttentionRef = useRef(performance.now());

// ==========================================================
// AVA ATTENTION STATE
// ==========================================================


const conversationModeRef = useRef(false);

// ==========================================================
// AVA EXPRESSION HOLD TIMER
// ==========================================================

const expressionUntilRef = useRef(0);



// ==========================================================
// TEMP EXPRESSION TEST API
// Remove after backend emotion routing is connected.
// ==========================================================

useEffect(() => {

  (window as any).avaExpression = (expr: string) => {

    switch ((expr || "").toUpperCase()) {

      case "HAPPY":
      case "JOY":
      case "SMILE":
      case "LAUGH":
      case "PROUD":
        expressionStateRef.current = "HAPPY";
        break;

      case "SAD":
      case "UPSET":
      case "DEPRESSED":
      case "LONELY":
      case "CRY":
        expressionStateRef.current = "SAD";
        break;

      case "SURPRISED":
      case "SHOCK":
      case "AMAZED":
      case "WOW":
        expressionStateRef.current = "SURPRISED";
        break;

      case "THINKING":
      case "CURIOUS":
      case "FOCUSED":
      case "ANALYZING":
        expressionStateRef.current = "THINKING";
        break;

      case "ANGRY":
      case "MAD":
      case "FRUSTRATED":
      case "ANNOYED":
        expressionStateRef.current = "ANGRY";
        break;

      case "CONFUSED":
      case "UNSURE":
      case "PUZZLED":
        expressionStateRef.current = "CONFUSED";
        break;

      default:
        expressionStateRef.current = "NEUTRAL";
        break;

    }

    expressionUntilRef.current = performance.now() + 3000;

    if (lastExpressionLogRef.current !== expressionStateRef.current) {
      lastExpressionLogRef.current = expressionStateRef.current;
      // Production: AVA EXPRESSION debug log removed
    }

  };

}, []);



const speakingRef = useRef(false);


useEffect(() => {

  const onSpeaking = (event: Event) => {
    const e = event as CustomEvent<{ speaking?: boolean }>;
    speakingRef.current = !!e.detail?.speaking;
  };

  
window.addEventListener("ava-speaking", onSpeaking as EventListener);

// ==========================================================
// AVA EXPRESSION EVENT BRIDGE
// ==========================================================

const onExpression = (event: Event) => {

    const e = event as CustomEvent<{expression?:string}>;

// Production debug removed
expressionUntilRef.current = performance.now() + 3000;

    switch ((e.detail?.expression || "").toUpperCase()) {

        case "HAPPY":
        case "SMILE":
            expressionStateRef.current = AVA_EXPRESSION_HAPPY;
            expressionUntilRef.current = performance.now() + 2500;
            break;

        case "SAD":
            expressionStateRef.current = AVA_EXPRESSION_SAD;
            expressionUntilRef.current = performance.now() + 2500;
            break;

        case "SURPRISED":
            expressionStateRef.current = AVA_EXPRESSION_SURPRISED;
            expressionUntilRef.current = performance.now() + 2500;
            break;

        case "THINKING":
            expressionStateRef.current = AVA_EXPRESSION_THINKING;
            break;

        case "ANGRY":
            expressionStateRef.current = AVA_EXPRESSION_ANGRY;
            break;

        case "CONFUSED":
            expressionStateRef.current = AVA_EXPRESSION_CONFUSED;
            break;

        default:
            expressionStateRef.current = AVA_EXPRESSION_NEUTRAL;
            break;
    }

    // Production: AVA STATE debug log removed

};

window.addEventListener(
    "ava-expression",
    onExpression as EventListener
);


  return () => {
    window.removeEventListener(
      "ava-speaking",
      onSpeaking as EventListener
    );

    window.removeEventListener(
      "ava-expression",
      onExpression as EventListener
    );
  };

}, []);


const startupRef = useRef(performance.now());

// ==========================================================
// TEMP MORPH DEBUG API
// ==========================================================

useEffect(() => {

    (window as any).avaMorph = (index:number) => {

        const influences = mouthInfluencesRef.current;

        if (!influences) {
            console.warn("No morph influences.");
            return;
        }

        for (let i = 0; i < influences.length; i++) {
            influences[i] = 0;
        }

        if (index >= 0 && index < influences.length) {
            influences[index] = 1.0;
        }

// Production debug removed
};

}, []);







  


const { scene, nodes, materials } = useGLTF("/models/AVA_REVISED.glb") as any;

/*
 * AVAAIO Ava graceful startup readiness gate.
 *
 * The GLB can finish parsing before iPhone/iPad WKWebView has
 * completed canvas sizing and stable GPU frames. Keep Ava hidden
 * until meaningful geometry, bounds, canvas dimensions, and several
 * consecutive healthy frames are confirmed.
 */
useEffect(() => {
  if (!scene) return;

  let disposed = false;
  let frameId = 0;
  let stableFrames = 0;
  let attempts = 0;

  const MAX_ATTEMPTS = 240;
  const REQUIRED_STABLE_FRAMES = 6;

  const probeReadiness = () => {
    if (disposed) return;

    attempts += 1;

    let meshCount = 0;
    let visibleMeshCount = 0;

    scene.traverse((obj: any) => {
      if (!obj?.isMesh) return;

      meshCount += 1;

      if (obj.visible !== false) {
        visibleMeshCount += 1;
      }
    });

    const bounds = new THREE.Box3().setFromObject(scene);
    const size = new THREE.Vector3();
    bounds.getSize(size);

    const finiteBounds =
      Number.isFinite(size.x) &&
      Number.isFinite(size.y) &&
      Number.isFinite(size.z);

    const meaningfulBounds =
      finiteBounds &&
      Math.max(size.x, size.y, size.z) > 0.01;

    const canvas =
      document.querySelector(
        ".ava-wrapper canvas"
      ) as HTMLCanvasElement | null;

    const canvasWidth =
      canvas?.clientWidth ||
      canvas?.width ||
      0;

    const canvasHeight =
      canvas?.clientHeight ||
      canvas?.height ||
      0;

    const canvasReady =
      canvasWidth >= 2 &&
      canvasHeight >= 2;

    const sceneReady =
      meshCount > 0 &&
      visibleMeshCount > 0 &&
      meaningfulBounds &&
      canvasReady;

    if (sceneReady) {
      stableFrames += 1;
    } else {
      stableFrames = 0;
    }

    if (stableFrames >= REQUIRED_STABLE_FRAMES) {
      console.log(
        "[AVA] Graceful readiness passed",
        {
          meshCount,
          visibleMeshCount,
          canvasWidth,
          canvasHeight,
          stableFrames,
        }
      );

      window.dispatchEvent(
        new CustomEvent("ava-geometry-ready", {
          detail: {
            meshCount,
            visibleMeshCount,
            canvasWidth,
            canvasHeight,
          },
        })
      );

      return;
    }

    if (attempts < MAX_ATTEMPTS) {
      frameId =
        window.requestAnimationFrame(
          probeReadiness
        );
      return;
    }

    console.warn(
      "[AVA] Graceful readiness still waiting",
      {
        meshCount,
        visibleMeshCount,
        canvasWidth,
        canvasHeight,
      }
    );
  };

  frameId =
    window.requestAnimationFrame(
      probeReadiness
    );

  return () => {
    disposed = true;

    if (frameId) {
      window.cancelAnimationFrame(frameId);
    }
  };
}, [scene]);

useEffect(() => {
  console.log("[AVA iPad Diagnostic]");
  console.log("scene =", scene);
  console.log("children =", scene?.children?.length);
  console.log("nodes =", Object.keys(nodes || {}).length);
  console.log("materials =", Object.keys(materials || {}).length);
}, [scene]);




useEffect(()=>{

    if(!scene) return;

    // AVA_IPAD_MATERIAL_FREEZE_V5
    // Preserve the GLB's original loaded materials on iPad.
    if (IPAD_VISUAL_RESTRICTIONS) {
      console.log("[AVA] iPad V5: material enhancer bypassed");
      return;
    }

    enhanceAvaMaterials(
        scene,
        AVA_BUILD23_VISUAL_STYLE
    );

},[scene]);

useEffect(() => {
// Production debug removed
Object.keys(nodes || {}).sort().forEach((k) => {
    const n = nodes[k];
// Production debug removed
});
}, [nodes]);



useEffect(() => {
  if (!scene) return;

  // Production debug removed
// Production debug removed
let meshes = 0;

  scene.traverse((o: any) => {

// Production debug removed
if (o?.isMesh) {
      meshes++;
// Production debug removed
}
  });

  // Production debug removed
}, [scene]);


  useEffect(() => {
    if (!scene) return;

    const box = new THREE.Box3().setFromObject(scene);
    const size = new THREE.Vector3();
    const center = new THREE.Vector3();

    box.getSize(size);
    box.getCenter(center);

    // Production debug removed
// Production debug removed
// Production debug removed
(window as any).__AVA_BOUNDS = {
      size,
      center,
      position: scene.position,
      scale: scene.scale,
    };

    // Production debug removed


// Production debug removed
// Production debug removed
// Production debug removed
// Production debug removed
}, [scene]);


useEffect(() => {
  if (!scene) return;

  let meshes = 0;

  scene.traverse((o:any) => {
    if (o?.isMesh) meshes++;
  });

  // Production debug removed
  // Production debug removed
  // Production debug removed
}, [scene]);

  // === CANONICAL THEME ACCENT CONSUMER ===
  // ThemeContext owns the theme.
  // Ava consumes accentColor and does not rewrite global CSS.
  useEffect(() => {
    if (!scene) return;

    if (IPAD_VISUAL_RESTRICTIONS) {
      return;
    }

    try {
      const color = sanitizeThreeColor(
        accentColor || getAvaAccentColor()
      );

      const applyColorToMaterial = (material: any) => {
        if (!material) return;

        if (Array.isArray(material)) {
          material.forEach(applyColorToMaterial);
          return;
        }

        const materialName =
          String(material?.name || "").toLowerCase();

        if (
          materialName.includes("glass") ||
          materialName.includes("lens")
        ) {
          return;
        }

        if (material.color) {
          material.color.set(color);
        }

        if (material.emissive) {
          material.emissive.set(color);
        }

        material.userData = material.userData || {};
        material.userData.__avaaioThemeEyeColor = color;

        material.needsUpdate = true;
      };

      scene.traverse((child: any) => {
        if (!child?.isMesh || !child?.material) return;

        const materials = Array.isArray(child.material)
          ? child.material
          : [child.material];

        const name =
          String(child.name || "").toLowerCase();

        const isThemeAccentPart =
          materials.some((material: any) => {
            const materialName =
              String(material?.name || "").toLowerCase();

            return (
              name.includes("eye") ||
              materialName.includes("eye") ||
              name.includes("emissive") ||
              materialName.includes("emissive") ||
              name.includes("core") ||
              materialName.includes("core") ||
              name.includes("center") ||
              materialName.includes("center") ||
              name.includes("cube033") ||
              name.includes("cube035")
            );
          });

        if (!isThemeAccentPart) return;

        applyColorToMaterial(child.material);
      });
    } catch (error) {
      console.warn(
        "Ava canonical theme accent update failed",
        error
      );
    }
  }, [scene, accentColor]);

  const neuralMeshesRef = useRef<
    { mesh: THREE.Mesh; mat: any; base: number; seed: number; role: "top" | "side" | "mouth" }[]
  >([]);
  const tmpV = useMemo(() => new THREE.Vector3(), []);

  const finalEyeOpen = Boolean(avaEyeOpen ?? eyeOpen);

  useEffect(() => {
    if (!scene) return;
    // 📱 iPad WKWebView: skip ALL material mutations to prevent GPU shader crash
    // AVA_IPAD_MATERIAL_FREEZE_V5
    if (IPAD_VISUAL_RESTRICTIONS) {
      return;
    }

    scene.traverse((child: any) => {
        if (child?.isMesh) {
          const mat = child.material;
          try {
            (window as any).__AVA_MESH_LOGS = (window as any).__AVA_MESH_LOGS || [];
            (window as any).__AVA_MESH_LOGS.push({
              name: child.name ?? null,
              type: child.type ?? null,
              material: child.material?.name ?? null
            });
            if ((window as any).__AVA_MESH_LOGS.length > 200) {
              (window as any).__AVA_MESH_LOGS = (window as any).__AVA_MESH_LOGS.slice(-200);
            }
          } catch {}

        }
      if (child?.isMesh && ["Cube033", "Cube035"].includes(String(child.name || ""))) {
        const mats = Array.isArray(child.material) ? child.material : [child.material];
        mats.forEach((mat: any) => {
          if (!mat) return;
          mat.color = new THREE.Color(getAvaAccentColor());
          mat.emissive = new THREE.Color(getAvaAccentColor());
          mat.emissiveIntensity = AVA_BUILD23_VISUAL_STYLE ? Math.min(finalEyeOpen ? 18.0 : 3.5, 4.5) : (finalEyeOpen ? 18.0 : 3.5);
          mat.transparent = false;
          mat.opacity = 1;
          mat.metalness = 0.0;
          mat.roughness = 0.12;
          if ("toneMapped" in mat) mat.toneMapped = false;
          if (!IPAD_VISUAL_RESTRICTIONS) {
            if (!IPAD_VISUAL_RESTRICTIONS) {
            }
          }
        });
        child.visible = true;
      }
      if (child?.isMesh && child.geometry) {
        child.geometry.computeBoundingBox?.();
        const bb = child.geometry.boundingBox;
        if (bb) {
          const size = new THREE.Vector3();
          bb.getSize(size);
          // ⚠️ DISABLED: was hiding important Ava meshes (mouth, sides, panels)
          // if (Math.max(size.x, size.y, size.z) > 2.0) child.visible = false;
        }
      }
    });
  }, [scene]);

  const textures = useTexture(
    AVA_BUILD23_VISUAL_STYLE
      ? {
          eye: "/textures/eye_emissive.png",
          mouth: "/textures/mouth_emissive.png",
          top: "/textures/top_emissive.png",
        }
      : {
          eye: "/textures/eye_emissive.png",
          mouth: "/textures/mouth_emissive.png",
          hornL: "/textures/horn_emissive_left.png",
          hornR: "/textures/horn_emissive_right.png",
          top: "/textures/top_emissive.png",
          sideL: "/textures/side_emissive_left.png",
          sideR: "/textures/side_emissive_right.png",
          propulsion: "/textures/propulsion_emission_map.png",
          section: "/textures/section_3-6_emissive_map.png",
        }
  );


  useEffect(() => {
    if (!scene) return;
    // 📱 iPad WKWebView: skip ALL material mutations to prevent GPU shader crash
    // AVA_IPAD_MATERIAL_FREEZE_V5
    if (IPAD_VISUAL_RESTRICTIONS) {
      return;
    }

    



// ==========================================================
// Production model rotation is controlled exclusively by the
// primary AVA_LIVING_PRESENCE_SYSTEM useFrame authority.
// ==========================================================

scene.traverse((obj: any) => {

      if (obj?.isMesh) {
        const mesh = String(obj.name || "");
        const mat = String(obj.material?.name || "");

        if (
          mesh.toLowerCase().includes("mouth") ||
          mesh.toLowerCase().includes("plane") ||
          mesh.toLowerCase().includes("face") ||
          mat.toLowerCase().includes("mouth") ||
          mat.toLowerCase().includes("face")
        ) {
          console.log(
            "[AVA MOUTH DEBUG]",
            "Mesh:", mesh,
            "| Material:", mat,
            "| Visible:", obj.visible
          );
        }
      }

        // =========================================
        // iOS WKWebView GPU MATERIAL FALLBACK
        // =========================================
          // =========================================
          // iPad WKWebView shader protection
          // =========================================
          if (
            IPAD_VISUAL_RESTRICTIONS &&
            obj?.isMesh &&
            obj.material
          ) {

            // IMPORTANT:
            // Do NOT replace materials on iPad.
            // Runtime material replacement causes
            // WebGL shader crashes inside WKWebView.

            if (Array.isArray(obj.material)) {

              obj.material.forEach((m: any) => {
                if (!m) return;

                m.needsUpdate = false;

                if ("toneMapped" in m) {
                  m.toneMapped = false;
                }
              });

            } else {

              obj.material.needsUpdate = false;

              if ("toneMapped" in obj.material) {
                obj.material.toneMapped = false;
              }
            }
          }


        // =========================================
        // AVA MODEL EXPRESSION INSPECTOR
        // =========================================
        try {

          if ((obj as any).isMesh || (obj as any).isSkinnedMesh) {


if ((obj as any).skeleton) {

            const boneNames =
              (obj as any).skeleton.bones.map(
                (b: any) => b.name
              );

            (window as any).__AVA_BONES__ =
              boneNames;

// Production debug removed
// Production debug removed
}

        }

      } catch (err) {

        console.warn(
          "⚠️ Morph inspector failed:",
          err
        );

      }

      

      // =========================================
      // MOUTH EMISSIVE MATERIAL PROBE
      // =========================================
      try {

        const mat = obj.material;

        const materialName =
          (mat?.name || "")
            .toString()
            .toLowerCase();

        if (
            materialName.includes(
              "mouth_emissive"
            )
          ) {}

      } catch (err) {

        console.warn(
          "Mouth material probe failed:",
          err
        );

      }



      // =========================================
      // CACHE AVA MOUTH SYSTEM
      // =========================================
      try {

        // =====================================================
        // DIRECT MORPH MESH BIND
        // =====================================================

        if (
          obj.name === "Plane005"
        ) {

          if (
            obj.updateMorphTargets
          ) {

            obj.updateMorphTargets();

          }

          

          mouthMeshRef.current = obj;

          mouthMaterialRef.current =
            Array.isArray(obj.material)
              ? obj.material[0]
              : obj.material;

          console.log("[AVA STARTUP]", {
            speaking: speakingRef.current,
            expression: expressionStateRef.current,
            opacity: mouthMaterialRef.current?.opacity,
            transparent: mouthMaterialRef.current?.transparent,
            visible: obj.visible
          });

          console.log(
            "[AVA MOUTH MATERIAL]",
            {
              type: mouthMaterialRef.current?.type,
              transparent: mouthMaterialRef.current?.transparent,
              opacity: mouthMaterialRef.current?.opacity,
              alphaTest: mouthMaterialRef.current?.alphaTest,
              visible: obj.visible,
              map: !!mouthMaterialRef.current?.map,
              alphaMap: !!mouthMaterialRef.current?.alphaMap,
              emissiveMap: !!mouthMaterialRef.current?.emissiveMap,
              color: mouthMaterialRef.current?.color,
              emissive: mouthMaterialRef.current?.emissive
            }
          );

          // Start hidden immediately so the mouth is not visible
          // during the first rendered frame after login.
          if (mouthMaterialRef.current) {
            mouthMaterialRef.current.transparent = true;
            mouthMaterialRef.current.opacity = 0.02;

            if ("depthWrite" in mouthMaterialRef.current)
              mouthMaterialRef.current.depthWrite = false;

            mouthMaterialRef.current.needsUpdate = true;
          }

          mouthMorphDictRef.current =
            obj.morphTargetDictionary;

          mouthInfluencesRef.current =
            obj.morphTargetInfluences;

// Production debug removed
// Production debug removed
// Production debug removed
// Production debug removed
// Production debug removed
// Production debug removed
// Production debug removed
}

      } catch (err) {

        console.warn(
          "Mouth cache failed:",
          err
        );

      }


if (!obj?.isMesh) return;

      const swapOne = (m: any) => {
        if (!m) return m;
        const rawName = (m.name || "").toString();
        if (rawName !== LENS_MATERIAL_NAME) return m;

        const basic = new THREE.MeshBasicMaterial({
          color: new THREE.Color(getAvaAccentColor()),
          transparent: true,
          depthWrite: false,
          opacity: 0.38,
        });
        basic.name = LENS_MATERIAL_NAME;
        return basic;
      };

      if (Array.isArray(obj.material)) obj.material = obj.material.map(swapOne);
      else obj.material = swapOne(obj.material);

      (obj as any).materialNeedsUpdate = true;
    });
  }, [scene, finalEyeOpen]);

  useEffect(() => {
    const isEyeEmissiveName = (nameLower: string) =>
      EYE_EMISSIVE_HINTS.some((h) => nameLower.includes(h));

    // GLOBAL LIVE THEME COLOR




    const isEmissiveTarget = (rawName: string) => {
      const nl = rawName.toLowerCase();
      return EMISSIVE_TARGET_HINTS.some((h) => nl.includes(h));
    };

    // 📱 iPad WKWebView: skip materials.forEach bulk rewrite
    // AVA_IPAD_MATERIAL_FREEZE_V5
    if (IPAD_VISUAL_RESTRICTIONS) {
      return;
    }
    Object.values(materials).forEach((mat: any) => {
      if (!mat) return;

      const rawName = (mat.name || "").toString();
      const nameLower = rawName.toLowerCase().trim();

      // =====================================================
      // AVA MOUTH MATERIAL
      // Keep the mouth hidden until the speech controller
      // fades it in.
      // =====================================================
      if (nameLower === "mouth_emissive") {
        mat.transparent = true;
        mat.opacity = 0.02;

        if ("depthWrite" in mat)
          mat.depthWrite = false;

        if ("toneMapped" in mat)
          mat.toneMapped = false;

        if (!IPAD_VISUAL_RESTRICTIONS)
          mat.needsUpdate = true;

        return;
      }

      if (rawName === LENS_MATERIAL_NAME) return;

      const rawNameLower = rawName.toLowerCase();
      const isLensTarget = rawName === LENS_MATERIAL_NAME;
      const isExactEyeEmissive = rawNameLower === "eye_emissive";

      if (isLensTarget) {
        mat.transparent = false;
        mat.opacity = 1;
        mat.color = new THREE.Color(getAvaAccentColor());
        mat.emissive = new THREE.Color(getAvaAccentColor());
        mat.emissiveIntensity = 0.0;
        mat.roughness = 0.22;
        mat.metalness = 0.10;

        if ("toneMapped" in mat) mat.toneMapped = false;
        if ("depthWrite" in mat) mat.depthWrite = true;
      }

      if (isExactEyeEmissive) {
        mat.transparent = false;
        mat.opacity = 1;
        mat.color = new THREE.Color(getAvaAccentColor());
        mat.emissive = new THREE.Color(getAvaAccentColor());
        mat.emissiveIntensity = AVA_BUILD23_VISUAL_STYLE ? Math.min(finalEyeOpen ? 16.0 : 1.0, 4.5) : (finalEyeOpen ? 16.0 : 1.0);
        mat.roughness = 0.08;
        mat.metalness = 0.00;

        if ("toneMapped" in mat) mat.toneMapped = false;
        if ("depthWrite" in mat) mat.depthWrite = true;

        if (mat.map) mat.map = null;
      }

      if (!mat.emissiveMap) {
      mat.transparent = true;
      mat.opacity = 1;
      mat.color = new THREE.Color(BASE_GUNMETAL);
      mat.metalness = NON_LENS_METALNESS;
      mat.roughness = NON_LENS_ROUGHNESS;
    }

      if (rawName === "WHITE MATERIAL") {
        mat.color = new THREE.Color(PANEL_WHITE);
        mat.metalness = 0.10;
        mat.roughness = 0.72;
      }

      if (rawName.toLowerCase() === "eye_emissive") {
        mat.map = null;
        mat.color = new THREE.Color(getAvaAccentColor());
        mat.emissive = new THREE.Color(getAvaAccentColor());
        mat.emissiveIntensity = AVA_BUILD23_VISUAL_STYLE ? Math.min(finalEyeOpen ? 18.0 : 1.2, 4.5) : (finalEyeOpen ? 18.0 : 1.2);
        if ("toneMapped" in mat) mat.toneMapped = false;
        mat.alphaMap = null;
        mat.emissiveMap = null;
        mat.transparent = false;
        mat.opacity = 1;
        mat.depthWrite = true;
        mat.toneMapped = false;
        mat.side = THREE.DoubleSide;
        mat.color = new THREE.Color(getAvaAccentColor());
        if (eyeOpen) {
          mat.emissive = new THREE.Color(getAvaAccentColor());
          mat.emissiveIntensity = AVA_BUILD23_VISUAL_STYLE ? 4.5 : 12.0;
        } else {
          mat.emissive = new THREE.Color(getAvaAccentColor());
          mat.emissiveIntensity = AVA_BUILD23_VISUAL_STYLE ? 4.5 : 5.5;
        }
        mat.metalness = 0.0;
        mat.roughness = 0.0;
      }
      if (nameLower.includes("mouth")) mat.emissiveMap = null;

      if (nameLower.includes("horn")) {
        // 🚫 BLOCK ALL FURTHER MODIFICATIONS
        mat.userData = mat.userData || {};
        mat.userData.__isHornLED = true;
        if (nameLower.includes("left") || nameLower.includes("_l") || nameLower.includes(".l")) {
          mat.emissiveMap = textures.hornL;
        } else if (nameLower.includes("right") || nameLower.includes("_r") || nameLower.includes(".r")) {
          mat.emissiveMap = textures.hornR;
        } else {
          mat.emissiveMap = textures.hornL || textures.hornR;
        }

        mat.color = new THREE.Color(getAvaAccentColor());
        mat.emissive = new THREE.Color(getAvaAccentColor());
        mat.emissiveIntensity = AVA_BUILD23_VISUAL_STYLE ? Math.min(finalEyeOpen ? 28.0 : 4.0, 4.5) : (finalEyeOpen ? 28.0 : 4.0);
        mat.metalness = 0.0;
        mat.roughness = 0.18;
        if ("toneMapped" in mat) mat.toneMapped = false;
        return;
      }

      if (nameLower.includes("top")) mat.emissiveMap = textures.top;

      if (nameLower.includes("side") || nameLower.includes("left"))
        mat.emissiveMap = textures.sideL;

      if (nameLower.includes("right"))
        mat.emissiveMap = textures.sideR;

      if (nameLower.includes("propulsion"))
        mat.emissiveMap = textures.propulsion;

      if (nameLower.includes("section") || nameLower.includes("3-6"))
        mat.emissiveMap = textures.section;


      if ((isEmissiveTarget(rawName) || mat.emissiveMap) && !mat.userData?.__isHornLED) {
        // 🚫 DO NOT override eye (already handled above)
        if (isEyeEmissiveName(nameLower)) {

          // use shared new THREE.Color(getAvaAccentColor()) from outer scope

          if (mat.emissive) {
            mat.emissive.copy(new THREE.Color(getAvaAccentColor()));
          } else {
            mat.emissive = new THREE.Color(getAvaAccentColor());
          }

          // skip — eye already configured
        } else {
          // removed flat emissive override to restore texture detail
          if (nameLower.includes("top")) {
        mat.emissiveIntensity = AVA_BUILD23_VISUAL_STYLE ? Math.min(finalEyeOpen ? 9.5 : 2.0, 4.5) : (finalEyeOpen ? 9.5 : 2.0); // 🔥 TOP strong
      } else if (nameLower.includes("side")) {
        mat.emissiveIntensity = AVA_BUILD23_VISUAL_STYLE ? Math.min(finalEyeOpen ? 6.5 : 1.5, 4.5) : (finalEyeOpen ? 6.5 : 1.5);
      } else if (nameLower.includes("propulsion")) {
        mat.emissiveIntensity = AVA_BUILD23_VISUAL_STYLE ? Math.min(finalEyeOpen ? 7.5 : 1.8, 4.5) : (finalEyeOpen ? 7.5 : 1.8);
      } else {
        mat.emissiveIntensity = AVA_BUILD23_VISUAL_STYLE ? Math.min(finalEyeOpen ? 5.5 : 1.4, 4.5) : (finalEyeOpen ? 5.5 : 1.4);
      }
        }
      }

      if (ALSO_NO_SPECULAR.has(rawName)) {
        mat.metalness = 0.0;
        mat.roughness = 1.0;
        if (typeof (mat as any).envMapIntensity !== "undefined") (mat as any).envMapIntensity = 0.0;
        if (typeof (mat as any).clearcoat !== "undefined") (mat as any).clearcoat = 0.0;
        if (typeof (mat as any).specularIntensity !== "undefined") (mat as any).specularIntensity = 0.0;
        if (typeof (mat as any).reflectivity !== "undefined") (mat as any).reflectivity = 0.0;
      } else {
        if (typeof (mat as any).envMapIntensity !== "undefined") {
          (mat as any).envMapIntensity = AVA_BUILD23_VISUAL_STYLE ? 0.0 : 0.15;
        }
      }
    });
  }, [materials, textures]);


  useEffect(() => {
    if (!scene) return;
    // 📱 iPad WKWebView: skip ALL material mutations to prevent GPU shader crash
    // AVA_IPAD_MATERIAL_FREEZE_V5
    if (IPAD_VISUAL_RESTRICTIONS) {
      return;
    }

    const hiddenEyeOccluders: string[] = [];

        // =========================================
      // iOS SAFE MODE
      // Skip ALL runtime material mutation
      // =========================================
      if (IPAD_VISUAL_RESTRICTIONS) {
// Production debug removed
return;
      }

  scene.traverse((obj: any) => {
      if (!obj || !obj.isMesh) return;

      const meshName = String(obj.name || "").toLowerCase();
      const matName = String(obj.material?.name || "").toLowerCase();

      const isExactEye = obj === nodes?.Cube027;
      if (isExactEye) return;

      const looksLikeEyeLayer =
        meshName.includes("eye") ||
        meshName.includes("lens") ||
        meshName.includes("visor") ||
        meshName.includes("glass") ||
        meshName.includes("orb") ||
        matName.includes("eye") ||
        matName.includes("lens") ||
        matName.includes("visor") ||
        matName.includes("glass") ||
        matName.includes("orb");

      if (!looksLikeEyeLayer) return;

      // hide likely front-cover layers so Cube027 can be seen
      obj.visible = false;
    });

    scene.traverse((obj: any) => {
      if (!obj || !obj.isMesh) return;

      const meshName = String(obj.name || "");
      const matName = String(obj.material?.name || "");

      const looksLikeEyeLayer =
        meshName.toLowerCase().includes("eye") ||
        meshName.toLowerCase().includes("lens") ||
        meshName.toLowerCase().includes("visor") ||
        meshName.toLowerCase().includes("glass") ||
        meshName.toLowerCase().includes("orb") ||
        matName.toLowerCase().includes("eye") ||
        matName.toLowerCase().includes("lens") ||
        matName.toLowerCase().includes("visor") ||
        matName.toLowerCase().includes("glass") ||
        matName.toLowerCase().includes("orb");

      if (!looksLikeEyeLayer) return;
      if (obj === nodes?.Cube027) return;

      // 🔥 FIX: only hide eye layers for FREE users
      if (!finalEyeOpen) {
        obj.visible = false;
        hiddenEyeOccluders.push(`${meshName || "(no-mesh-name)"} | ${matName || "(no-material-name)"}`);
      } else {
        obj.visible = true;
      }
    });


  }, [scene, nodes]);

  useEffect(() => {
    if (!scene) return;
    // 📱 iPad WKWebView: skip ALL material mutations to prevent GPU shader crash
    // AVA_IPAD_MATERIAL_FREEZE_V5
    if (IPAD_VISUAL_RESTRICTIONS) {
      return;
    }

    scene.traverse((obj: any) => {
      if (!obj || !obj.isMesh) return;

      const meshName = String(obj.name || "").toLowerCase();
      const matName = String(obj.material?.name || "").toLowerCase();

      const isProCarbonEyeShell =
    matName === "pro_v77_eye_shell_lower_carbon" ||
    matName === "pro_v77_eye_shell_upper_carbon";

  const looksLikeVisibleEye =
    !isProCarbonEyeShell &&
    (
      meshName.includes("eye") ||
      meshName.includes("orb") ||
      meshName.includes("lens") ||
      matName.includes("eye") ||
      matName.includes("orb") ||
      matName.includes("lens")
    );

      const isGlass =
        meshName.includes("glass") ||
        matName.includes("glass");

      if (!looksLikeVisibleEye) return;

      const applyAvaAccentToVisibleEye = (material: any) => {
        if (!material) return;
        const color = getAvaAccentColor();

        if (Array.isArray(material)) {
          material.forEach(applyAvaAccentToVisibleEye);
          return;
        }

        if (!material.color) material.color = new THREE.Color(color);
        else material.color.set(color);

        if (!material.emissive) material.emissive = new THREE.Color(color);
        else material.emissive.set(color);

        material.emissiveIntensity = AVA_BUILD23_VISUAL_STYLE ? Math.min(finalEyeOpen ? 12.0 : 3.0, 4.5) : (finalEyeOpen ? 12.0 : 3.0);

        // LIVE THEME COLOR OVERRIDE
        if (material.emissive) {
          material.emissive.set(getAvaAccentColor());
        } else {
          material.emissive = new THREE.Color(getAvaAccentColor());
        }

        material.transparent = false;
        material.opacity = 1;
        material.toneMapped = false;
        if (!IPAD_VISUAL_RESTRICTIONS) {
            material.needsUpdate = true;
          }
      };

      applyAvaAccentToVisibleEye(obj.material);

// 🔥 FIX: allow glass layer when eye is open
if (isGlass && !finalEyeOpen) return;

if (isGlass && AVA_BUILD23_VISUAL_STYLE) {
  try {
    obj.material.transparent = false;
    obj.material.opacity = 1;
    obj.material.envMap = null;

    if (typeof obj.material.reflectivity !== "undefined") {
      obj.material.reflectivity = 0;
    }

    if (typeof obj.material.transmission !== "undefined") {
      obj.material.transmission = 0;
    }

    if (typeof obj.material.ior !== "undefined") {
      obj.material.ior = 1;
    }

    obj.material.metalness = 0;
    obj.material.roughness = 1;
  } catch (e) {
    console.warn("iOS glass stabilization failed", e);
  }
}


      // 🔥 FIX: do NOT replace material — update it instead
if (obj.material) {
  try {
    const color = getAvaAccentColor();
    if (obj.material.color) obj.material.color.set(color);
    if (obj.material.emissive) obj.material.emissive.set(color);
      if (!IPAD_VISUAL_RESTRICTIONS) {
        obj.material.needsUpdate = true;
      }
  } catch (e) {
    console.warn("material update failed", e);
  }
}

// 🔋 LOW POWER FLICKER (free mode only)
if (!finalEyeOpen && obj.material?.emissive) {
  const rawFlicker =
    0.18 + Math.sin(performance.now() * 0.002) * 0.08;

  const safeFlicker = Number.isFinite(rawFlicker)
    ? THREE.MathUtils.clamp(rawFlicker, 0, 4)
    : 0.18;

  obj.material.emissiveIntensity = safeFlicker;
}

      if (obj.material) {
        const name = (obj.material.name || "").toLowerCase();

        const isEmissive =
          name.includes("emissive") ||
          name.includes("circuit") ||
          name.includes("strip") ||
          name.includes("side") ||
          name.includes("propulsion") ||
          name.includes("horn") ||
          name.includes("mouth") ||
          name.includes("top");

        // ONLY strip maps from NON-emissive materials
        if (!isEmissive) {
          obj.material.map = null;
          obj.material.alphaMap = null;
          obj.material.aoMap = null;
          obj.material.bumpMap = null;
          obj.material.normalMap = null;
          obj.material.roughnessMap = null;
          obj.material.metalnessMap = null;
          obj.material.envMap = null;
        }

          if (!IPAD_VISUAL_RESTRICTIONS) {
            obj.material.needsUpdate = true;
          }
        }


      obj.renderOrder = 100;

      // Preserve speech-controlled mouth visibility.
      if (obj.name !== "Plane005") {
        obj.visible = true;
      }
    });
  }, [scene, finalEyeOpen]);



  useEffect(() => {
    if (!scene) return;
    // 📱 iPad WKWebView: skip ALL material mutations to prevent GPU shader crash
    // AVA_IPAD_MATERIAL_FREEZE_V5
    if (IPAD_VISUAL_RESTRICTIONS) {
      return;
    }

    const isNeuralMat = (name: string) => {
      const nl = name.toLowerCase();

      // Eye/lens/orb are NOT circuits. They keep their own steady color.
      if (
        nl.includes("eye") ||
        nl.includes("lens") ||
        nl.includes("orb")
      ) {
        return false;
      }

      return (
        nl.includes("emissive") ||
        nl.includes("circuit") ||
        nl.includes("strip") ||
        nl.includes("side") ||
        nl.includes("propulsion") ||
        nl.includes("horn") ||
        nl.includes("mouth") ||
        nl.includes("top")
      );
    };

    const list: {
      mesh: THREE.Mesh;
      mat: any;
      base: number;
      seed: number;
      role: "top" | "side" | "mouth";
    }[] = [];

    scene.traverse((obj: any) => {
      if (!obj?.isMesh) return;

      // =========================================
      // REAL MORPH TARGET PROBE
      // =========================================

      try {

        if (
          obj.morphTargetDictionary ||
          obj.morphTargetInfluences
        ) {

          

        }

      } catch (err) {

        console.warn(
          "Morph probe failed:",
          err
        );

      }

      const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
      mats.forEach((m: any) => {
        if (!m) return;
        if (m.name === LENS_MATERIAL_NAME) return;
        if (!m.emissive) return;
        const meshNameLower = String(obj.name || "").toLowerCase();
          const matNameLower = String(m.name || "").toLowerCase();

          // Eye/lens/orb meshes must not join neural circuit animation.
          if (
            meshNameLower.includes("eye") ||
            meshNameLower.includes("lens") ||
            meshNameLower.includes("orb") ||
            matNameLower.includes("eye") ||
            matNameLower.includes("lens") ||
            matNameLower.includes("orb")
          ) return;

          if (!isNeuralMat(m.name || "")) return;

        const nl = (m.name || "").toLowerCase();
        const role: "top" | "side" | "mouth" =
          nl.includes("mouth") ? "mouth" : nl.includes("side") ? "side" : "top";

        const base = role === "mouth" ? 2.75 : role === "side" ? 2.45 : 2.25;
        const seed = hash11((obj.uuid || "").length * 0.19 + (m.name || "").length * 0.37);

        list.push({ mesh: obj as THREE.Mesh, mat: m, base, seed, role });
      });
    });

    neuralMeshesRef.current = list;
  }, [scene]);


// SECOND useFrame DISABLED — merged into primary authority

/* AVA_LIVING_PRESENCE_SYSTEM */
useFrame((state) => {
  if (!group.current) return;

  const t = state.clock.getElapsedTime();

  const speakingFocus = speakingRef.current ? 1.0 : 0.0;

  if (speakingRef.current ||
    Math.abs(look.x) > 0.01 ||
    Math.abs(look.y) > 0.01
) {
    lastAttentionRef.current = performance.now();
  }

  const attentionAge =
    (performance.now() - lastAttentionRef.current) / 1000;

  const attentionBlend =
    attentionAge < 30
      ? 1.0
      : 0.0;

const idleWanderYaw =
    attentionAge < 30
      ? 0
      : Math.sin(t * 0.18) * 0.06;

const idleWanderPitch =
    attentionAge < 30
      ? 0
      : Math.cos(t * 0.12) * 0.05;



  const motionSpeed =
    Math.abs(look.x - naturalLookRef.current.yaw) +
    Math.abs(look.y - naturalLookRef.current.pitch);

  const trackingBoost = THREE.MathUtils.clamp(
    0.24 + motionSpeed * 0.9,
    0.24,
    0.34
  );

  const idleYaw =
    Math.sin(t * 0.34) * 0.035 +
    Math.sin(t * 0.17 + 1.8) * 0.018;

  const idlePitch =
    Math.cos(t * 0.28 + 0.6) * 0.010;

  const speechNod = speakingRef.current
    ? Math.sin(t * 1.15) * 0.006 +
      Math.sin(t * 2.35) * 0.002
    : 0;

  const speechTilt = speakingRef.current
    ? Math.sin(t * 0.78 + 0.9) * 0.011
    : Math.sin(t * 0.16) * 0.008;
  const startupAge =
    (performance.now() - startupRef.current) / 1000;

  /*
   * Vision user reacquisition controller.
   *
   * 0.0–1.6 seconds:
   *   smoothly return from the last known user position.
   *
   * After 1.6 seconds:
   *   begin a small, natural search around center.
   *
   * Face detection immediately cancels search and restores
   * fast tracking.
   */
  const visionNow =
    typeof performance !== "undefined"
      ? performance.now()
      : Date.now();

  if (
    visionTrackingActive &&
    visionFaceDetected
  ) {
    visionLastFaceSeenRef.current =
      visionNow;
  }

  const visionFaceLostSeconds =
    visionTrackingActive &&
    !visionFaceDetected
      ? Math.max(
          0,
          (
            visionNow -
            visionLastFaceSeenRef.current
          ) / 1000
        )
      : 0;

  const visionReturnToCenter =
    THREE.MathUtils.clamp(
      (
        visionFaceLostSeconds -
        0.10
      ) / 1.60,
      0,
      1
    );

  const visionSearchTarget =
    visionFaceLostSeconds > 1.60
      ? THREE.MathUtils.clamp(
          (
            visionFaceLostSeconds -
            1.60
          ) / 1.80,
          0,
          1
        )
      : 0;

  visionSearchBlendRef.current =
    THREE.MathUtils.lerp(
      visionSearchBlendRef.current,
      visionFaceDetected
        ? 0
        : visionSearchTarget,
      visionFaceDetected
        ? 0.46
        : 0.045
    );

  const visionSearchBlend =
    visionSearchBlendRef.current;

  const visionSearchYaw =
    Math.sin(
      t * 0.72
    ) *
    0.16 *
    visionSearchBlend;

  const visionSearchPitch =
    (
      Math.sin(
        t * 0.43 + 1.25
      ) *
      0.055 +
      Math.sin(
        t * 0.21
      ) *
      0.025
    ) *
    visionSearchBlend;

  const visionLostYaw =
    THREE.MathUtils.lerp(
      -look.x,
      0,
      visionReturnToCenter
    ) +
    visionSearchYaw;

  const visionLostPitch =
    THREE.MathUtils.lerp(
      look.y,
      0,
      visionReturnToCenter
    ) +
    visionSearchPitch;

const centeredLook =
    visionTrackingActive
      ? (
          visionFaceDetected
            ? (-look.x)
            : visionLostYaw
        )
      : motionLookActive
        // Motion contributes most of the direction, while the
        // remaining center bias keeps Ava engaged with the user.
        ? (-look.x * 0.78)
        : (
            startupAge < 10
              ? (-look.x)
              : (
                  attentionAge >= 30
                    ? idleWanderYaw
                    : 0.0
                )
          );

const targetYaw = THREE.MathUtils.clamp(
    centeredLook +
    idleYaw * (1.0 - attentionBlend * 0.75),
    -0.42,
    0.42
);

  const centeredPitch =
    visionTrackingActive
      ? (
          visionFaceDetected
            ? look.y
            : visionLostPitch
        )
      : motionLookActive
        ? look.y * 0.82
        : (
            startupAge < 10
              ? look.y
              : (
                  attentionAge >= 30
                    ? idleWanderPitch
                    : 0.0
                )
          );

const pitchStrength =
    visionTrackingActive
      ? 0.68
      : motionLookActive
      ? speakingRef.current
        ? 0.58
        : 0.54
      : speakingRef.current
        ? 0.42
        : 0.36;

const targetPitch = THREE.MathUtils.clamp(
    AVA_NEUTRAL_HEAD_PITCH -
      centeredPitch * pitchStrength +
      idlePitch * (speakingRef.current ? 0.35 : 1.0) +
      speechNod,
    AVA_NEUTRAL_HEAD_PITCH - 0.42,
    AVA_NEUTRAL_HEAD_PITCH + 0.24
  );

  const targetRoll = THREE.MathUtils.clamp(
    speechTilt,
    -0.035,
    0.035
  );

  const gazeFollowRate =
    visionTrackingActive
      ? (
          visionFaceDetected
            ? 0.42
            : 0.075
        )
      : motionLookActive
        ? 0.10
        : 0.24;

  const pitchFollowRate =
    visionTrackingActive
      ? (
          visionFaceDetected
            ? 0.40
            : 0.070
        )
      : motionLookActive
        ? 0.095
        : 0.25;

  const rollFollowRate =
    visionTrackingActive
      ? (
          visionFaceDetected
            ? 0.24
            : 0.055
        )
      : motionLookActive
        ? 0.09
        : 0.18;

  naturalLookRef.current.yaw = THREE.MathUtils.lerp(
    naturalLookRef.current.yaw,
    targetYaw,
    gazeFollowRate
  );

  naturalLookRef.current.pitch = THREE.MathUtils.lerp(
    naturalLookRef.current.pitch,
    targetPitch,
    pitchFollowRate
  );

  naturalLookRef.current.roll = THREE.MathUtils.lerp(
    naturalLookRef.current.roll,
    targetRoll,
    rollFollowRate
  );

  const AVA_HEIGHT_OFFSET = 0.14;

  group.current.position.y =
    position[1] +
    AVA_HEIGHT_OFFSET +
    Math.sin(t * 1.5) * 0.008 +
    (speakingRef.current ? Math.sin(t * 2.4) * 0.002 : 0);

  group.current.position.x = THREE.MathUtils.lerp(
    group.current.position.x,
    position[0] +
      naturalLookRef.current.yaw * 0.018 * speakingFocus,
    0.085
  );

  group.current.position.z = position[2];

  const physicalHeadFollow =
    visionTrackingActive
      ? (
          visionFaceDetected
            ? 0.46
            : 0.085
        )
      : motionLookActive
        ? 0.15
        : 0.34;

  const physicalPitchFollow =
    visionTrackingActive
      ? (
          visionFaceDetected
            ? 0.44
            : 0.080
        )
      : motionLookActive
        ? 0.14
        : 0.324;

  const physicalRollFollow =
    visionTrackingActive
      ? (
          visionFaceDetected
            ? 0.28
            : 0.060
        )
      : motionLookActive
        ? 0.12
        : 0.24;

  group.current.rotation.y = THREE.MathUtils.lerp(
    group.current.rotation.y,
    naturalLookRef.current.yaw,
    physicalHeadFollow
  );

  group.current.rotation.x = THREE.MathUtils.lerp(
    group.current.rotation.x,
    naturalLookRef.current.pitch,
    physicalPitchFollow
  );

  group.current.rotation.z = THREE.MathUtils.lerp(
    group.current.rotation.z,
    naturalLookRef.current.roll,
    physicalRollFollow
  );


  // ==========================================================
  // AVA MOUTH MORPH DRIVER
  // ==========================================================

  // ==========================================================
  // AVA RUNTIME EXPRESSION STATE
  // ==========================================================

  
if (
    performance.now() > expressionUntilRef.current &&
    expressionStateRef.current !== AVA_EXPRESSION_NEUTRAL
) {
    expressionStateRef.current = AVA_EXPRESSION_NEUTRAL;
}

switch (expressionStateRef.current) {

    case AVA_EXPRESSION_SMILE:
    case AVA_EXPRESSION_HAPPY:
        // Basis mesh is the smile.
        mouthExpressionRef.current = "SMILE";
        break;

    case AVA_EXPRESSION_SAD:
    case AVA_EXPRESSION_FROWN:
        mouthExpressionRef.current = "SAD";
        break;

    case AVA_EXPRESSION_SURPRISED:
    case "SHOCK":
        mouthExpressionRef.current = "SURPRISED";
        break;

    case AVA_EXPRESSION_ANGRY:
        mouthExpressionRef.current = "SAD";
        break;

    case AVA_EXPRESSION_THINKING:
        mouthExpressionRef.current = "NEUTRAL";
        break;

    case AVA_EXPRESSION_CONFUSED:
        mouthExpressionRef.current = "SURPRISED";
        break;

    default:
        mouthExpressionRef.current = "NEUTRAL";
        break;
}


  const influences = mouthInfluencesRef.current;


  if (influences && influences.length >= 3) {

    const open = THREE.MathUtils.clamp(
      THREE.MathUtils.lerp(
        influences[AVA_MORPH_NEUTRAL] ?? 0,
        speakingRef.current ? Math.max(0.08, mouthLevel) : 0,
        0.34
      ),
      0,
      1
    );

    // Mouth opening only.
    influences[AVA_MORPH_NEUTRAL] = open;

    // ==========================================================
    // AVA EXPRESSION BLENDING
    // ==========================================================

    let targetOpen = speakingRef.current
      ? Math.max(0.08, mouthLevel)
      : 0.0;

    let targetSad = 0.0;
    let targetSurprised = 0.0;

    
// Production: expression debug log removed

switch (mouthExpressionRef.current) {

      case "SMILE":
        // Basis already contains the smile.
        targetOpen = 0.0;
        targetSad = 0.0;
        targetSurprised = 0.0;
        break;

      case "SAD":
        targetOpen = Math.max(targetOpen, 0.05);
        targetSad = 1.0;
        break;

      case "SURPRISED":
        targetOpen = Math.max(targetOpen, 0.90);
        targetSurprised = 1.0;
        break;

      case "NEUTRAL":
        targetOpen = 1.0;
        break;

      default:
        break;

    }

    influences[AVA_MORPH_NEUTRAL] =
      THREE.MathUtils.lerp(
        influences[AVA_MORPH_NEUTRAL] ?? 0,
        targetOpen,
        0.24
      );

    influences[AVA_MORPH_SAD] =
      THREE.MathUtils.lerp(
        influences[AVA_MORPH_SAD] ?? 0,
        targetSad,
        0.22
      );

    influences[AVA_MORPH_SURPRISED] =
      THREE.MathUtils.lerp(
        influences[AVA_MORPH_SURPRISED] ?? 0,
        targetSurprised,
        0.22
      );

    // ==========================================================
    // AVA EXPRESSION COMPOSITOR
    // Adds unique motion to expressions without requiring
    // additional mouth morph targets.
    // ==========================================================

    if (group.current) {

      switch (expressionStateRef.current) {

        case AVA_EXPRESSION_THINKING:
          group.current.rotation.x +=
            Math.sin(t * 1.2) * 0.03;
          group.current.rotation.z += 0.10;
          break;

        case AVA_EXPRESSION_CONFUSED:
          group.current.rotation.z +=
            Math.sin(t * 3.5) * 0.12;
          group.current.rotation.y +=
            Math.sin(t * 2.0) * 0.04;
          break;

        case AVA_EXPRESSION_ANGRY:
          group.current.rotation.x -= 0.08;
          group.current.rotation.y *= 0.95;
          break;

        case AVA_EXPRESSION_HAPPY:
        case AVA_EXPRESSION_SMILE:
          group.current.rotation.z +=
            Math.sin(t * 2.8) * 0.02;
          break;

        case AVA_EXPRESSION_SURPRISED:
          group.current.rotation.x -= 0.05;
          group.current.position.y += 0.010;
          break;

      }

    }

  }

  // ==========================================================
  // AVA IDLE MOUTH VISIBILITY
  // ==========================================================

  const mouthMaterial = mouthMaterialRef.current;

  if (mouthMaterial) {

    // ==========================================================
    // Speech-only mouth visibility
    // Mouth stays hidden until Ava is actually speaking.
    // ==========================================================

    let targetOpacity = 0.02;

    if (speakingRef.current) {
      targetOpacity = 1.0;
    }

    mouthMaterial.transparent = true;

    mouthMaterial.opacity = THREE.MathUtils.lerp(
      mouthMaterial.opacity ?? 1.0,
      targetOpacity,
      speakingRef.current ? 0.22 : 0.10
    );

    if ("depthWrite" in mouthMaterial)
      mouthMaterial.depthWrite = false;

    mouthMaterial.needsUpdate = true;
  }

  // AVA MOUTH SPEECH ENERGY
  // ==========================================================
  for (const entry of neuralMeshesRef.current) {

    if (entry.role !== 'mouth') continue;
    if (!entry.mat) continue;

    const speechLevel = speakingRef.current ? 1.0 : 0.0;

    const pulse =
      0.5 + 0.5 * Math.sin(t * 16.0 + entry.seed * 8.0);

    const idleGlow = 0.06;

    const targetIntensity =
      idleGlow +
      entry.base * 0.18 +
      speechLevel * (2.2 + pulse * 1.4);

    try {
      if (entry.mat.emissive) {
        entry.mat.emissive.setRGB(
          0.18 + speechLevel * 0.10,
          0.88 + speechLevel * 0.10,
          1.00
        );
      }

      entry.mat.emissiveIntensity = THREE.MathUtils.clamp(
        THREE.MathUtils.lerp(
          entry.mat.emissiveIntensity ?? entry.base,
          targetIntensity,
          speakingRef.current ? 0.22 : 0.10
        ),
        0.02,
        8.0
      );

    } catch (e) {}
  }

});


/*
 * AVAAIO cold-start final readiness gate.
 * Keep the Canvas hidden until AvaModel runtime setup has mounted
 * and two additional browser frames have completed.
 */
useEffect(() => {
  if (!scene) return;

  let cancelled = false;
  let frame1 = 0;
  let frame2 = 0;

  const markFinalReady = () => {
    if (cancelled) return;

    frame1 = window.requestAnimationFrame(() => {
      frame2 = window.requestAnimationFrame(() => {
        if (cancelled) return;

        window.dispatchEvent(
          new CustomEvent("ava-render-ready", {
            detail: {
              source: "ava-model-final-ready",
            },
          })
        );
      });
    });
  };

  // GUARD 117 IPHONE FINAL READY FIX
  //
  // Normal iPhone can miss the earlier geometry-ready event.
  // This final gate is now mounted, so invoke its existing
  // two-frame readiness path directly for iPhone only.
  //
  // iPad keeps the established geometry/GPU-safe readiness path.
  if (
    isIOSWebKit() &&
    !IOS_GPU_SAFE_MODE &&
    !IS_IPAD_DEVICE
  ) {
    markFinalReady();

    return () => {
      cancelled = true;

      if (frame1) window.cancelAnimationFrame(frame1);
      if (frame2) window.cancelAnimationFrame(frame2);
    };
  }

  window.addEventListener(
    "ava-geometry-ready",
    markFinalReady,
    { once: true },
  );

  return () => {
    cancelled = true;

    window.removeEventListener(
      "ava-geometry-ready",
      markFinalReady,
    );

    if (frame1) window.cancelAnimationFrame(frame1);
    if (frame2) window.cancelAnimationFrame(frame2);
  };
}, [scene]);


return (
    <>

      {/* ========================================= */}
      {/* AVAAIO FIXED WORLDSPACE EMITTER */}
      {/* ========================================= */}

        <group
          position={[emitterOffsetX, -0.36, 0.18]}
          renderOrder={2}
        >



        {/* INNER SCAN RAIL */}
        <mesh
          rotation={[
            -Math.PI / 2,
            0,
            -pulseTime * 1.8,
          ]}
          position={[0, -0.52, 0.14]}
          renderOrder={4}
        >
          <torusGeometry args={[0.14, 0.0035, 18, 180]} />
          <meshBasicMaterial
            color={"#b8ffff"}
            transparent
            opacity={AVA_BUILD23_VISUAL_STYLE ? 0.12 : 0.55}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
            toneMapped={false}
          />
        </mesh>

        {/* ENERGY CORE LIGHT */}
        <pointLight
          color={"#8b5cff"}
          intensity={AVA_BUILD23_VISUAL_STYLE ? 0.7 : 1.8}
          distance={1.8}
          position={[0, 0.14, 0]}
        />
        {/* ENERGY STREAM */}

      </group>

      {/* ========================================= */}
      {/* AVA ANIMATED RIG */}
      {/* ========================================= */}

    <group
      ref={group}
      scale={scale}
      position={position}
      renderOrder={10}
    >
      <primitive object={scene} />

      <AvaVisionEye
        active={visionActive}
        eyeOpen={eyeOpen}
        status={visionStatus}
        captureCanvasFrameRef={visionCaptureCanvasFrameRef}
        targetMesh={
          nodes?.Cube027 as
            THREE.Mesh | undefined
        }
      />

      {/* HALO DISABLED: using internal emissive eye system instead */}
      <pointLight intensity={1.5} distance={3.1} position={[0, 0.28, 0.9]} />
        <pointLight intensity={1.1} distance={3.0} position={[0.35, 0.28, 0.7]} />
        {/* === SUBTLE MOUTH AREA GLOW - BOOSTED === */}
      <pointLight
        color={getAvaAccentColor()}
        intensity={speaking ? 1.65 : 0.42}
        distance={0.72}
        position={[0, -0.18, 0.42]}
      />
      {finalEyeOpen && !visionActive && (
        <pointLight
          color={getAvaAccentColor()}
          intensity={2.8}
          distance={2.4}
          position={[0, 0.82, 0.72]}
        />
      )}
    </group>
    </>
  );
}


// =====================================================
// AVA_IPAD_R3F_SIZE_AUTHORITY
// Keeps React Three Fiber's internal dimensions synchronized
// with Ava's real dashboard wrapper during an iPad landscape
// cold start. A physical rotation previously performed this
// synchronization indirectly.
// =====================================================
function IpadR3FSizeAuthority() {
  const { gl, camera, setSize, invalidate } = useThree();

  useEffect(() => {
    if (!IPAD_VISUAL_RESTRICTIONS) return;

    const canvas = gl.domElement;
    const wrapper = canvas.closest(".ava-wrapper") as HTMLElement | null;

    if (!wrapper) return;

    let raf = 0;
    const timers: number[] = [];

    const syncSize = () => {
  const rect = wrapper.getBoundingClientRect();

  const width = Math.max(
    2,
    Math.round(
      rect.width ||
      window.visualViewport?.width ||
      window.innerWidth
    )
  );

  const height = Math.max(
    2,
    Math.round(
      rect.height ||
      window.visualViewport?.height ||
      window.innerHeight
    )
  );

  // =====================================================
  // AVA_IPAD_PORTRAIT_PROJECTION_LOCK_V10B1
  //
  // Keep R3F/WebGL synchronized with the COMPLETE wrapper.
  // This preserves the V8 iPad sizing/recovery behavior.
  //
  // Only the 3D projection viewport changes in landscape.
  // Portrait remains the visual reference.
  // =====================================================

  setSize(width, height);

  const perspectiveCamera =
    camera as THREE.PerspectiveCamera;

  const isLandscape =
    width > height;

  if (!isLandscape) {
    const portraitAspect =
      width / height;

    try {
      window.sessionStorage.setItem(
        "avaaio-ipad-portrait-aspect-v10b1",
        String(portraitAspect)
      );
    } catch {
      // Never allow storage failure to affect rendering.
    }

    gl.setScissorTest(false);

    gl.setViewport(
      0,
      0,
      width,
      height
    );

    perspectiveCamera.aspect =
      portraitAspect;

    perspectiveCamera.updateProjectionMatrix();

    invalidate();
    return;
  }

  let portraitAspect = 0.75;

  try {
    const storedAspect = Number(
      window.sessionStorage.getItem(
        "avaaio-ipad-portrait-aspect-v10b1"
      )
    );

    if (
      Number.isFinite(storedAspect) &&
      storedAspect > 0.45 &&
      storedAspect < 1.0
    ) {
      portraitAspect = storedAspect;
    }
  } catch {
    // 4:3 iPad portrait fallback remains active.
  }

  const viewportHeight =
    height;

  const viewportWidth =
    Math.max(
      2,
      Math.min(
        width,
        Math.round(
          viewportHeight *
          portraitAspect
        )
      )
    );

  const viewportX =
    Math.max(
      0,
      Math.floor(
        (width - viewportWidth) / 2
      )
    );

  gl.setScissorTest(false);

  gl.setViewport(
    0,
    0,
    width,
    height
  );

  gl.clear(
    true,
    true,
    true
  );

  gl.setViewport(
    viewportX,
    0,
    viewportWidth,
    viewportHeight
  );

  gl.setScissor(
    viewportX,
    0,
    viewportWidth,
    viewportHeight
  );

  gl.setScissorTest(true);

  perspectiveCamera.aspect =
    viewportWidth /
    viewportHeight;

  perspectiveCamera.updateProjectionMatrix();

  invalidate();
};

const scheduleSync = () => {
      window.cancelAnimationFrame(raf);

      raf = window.requestAnimationFrame(() => {
        window.requestAnimationFrame(syncSize);
      });
    };

    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(scheduleSync)
        : null;

    observer?.observe(wrapper);

    window.addEventListener("resize", scheduleSync);
    window.addEventListener("orientationchange", scheduleSync);
    window.visualViewport?.addEventListener("resize", scheduleSync);

    // iPad Safari can settle dashboard dimensions over several
    // stages following the login redirect.
    [0, 100, 250, 500, 900, 1500, 2500].forEach((delay) => {
      timers.push(window.setTimeout(scheduleSync, delay));
    });

    return () => {
      window.cancelAnimationFrame(raf);
      timers.forEach((timer) => window.clearTimeout(timer));

      observer?.disconnect();

      window.removeEventListener("resize", scheduleSync);
      window.removeEventListener("orientationchange", scheduleSync);
      window.visualViewport?.removeEventListener(
        "resize",
        scheduleSync
      );
    };
  }, [camera, gl, invalidate, setSize]);

  return null;
}



// ==========================================================
// AVAAIO MOBILE UX PHASE 4A
// AVA SINGLE CENTRAL-EYE VISION DISPLAY
// ==========================================================
//
// Reuses the already captured Ava Vision frame. It does not
// request a second camera or alter the original Cube027 eye.
// The preview is a rollback-safe circular surface placed over
// Ava's existing central eye.
//
const AVA_VISION_EYE_RADIUS =
  HALO_RADIUS * 0.91;

const AVA_VISION_EYE_RING_RADIUS =
  HALO_RADIUS * 0.965;

const AVA_VISION_EYE_Z_OFFSET = 0.026;

const AVA_VISION_EYE_CANVAS_SIZE =
  IPAD_GPU_RESOURCE_SAFE ? 192 : 256;

const AVA_VISION_EYE_CAPTURE_MS =
  IPAD_GPU_RESOURCE_SAFE ? 360 : 240;

function configureAvaVisionEyeTexture(
  texture: THREE.Texture
) {
  const image =
    texture.image as {
      width?: number;
      height?: number;
      naturalWidth?: number;
      naturalHeight?: number;
    } | undefined;

  const width =
    Number(
      image?.naturalWidth ||
      image?.width ||
      0
    );

  const height =
    Number(
      image?.naturalHeight ||
      image?.height ||
      0
    );

  texture.colorSpace =
    THREE.SRGBColorSpace;

  texture.minFilter =
    THREE.LinearFilter;

  texture.magFilter =
    THREE.LinearFilter;

  texture.generateMipmaps = false;

  texture.wrapS =
    THREE.ClampToEdgeWrapping;

  texture.wrapT =
    THREE.ClampToEdgeWrapping;

  /*
   * Center-crop the camera frame into the circular eye
   * without stretching a 4:3 frame into a square.
   */
  if (width > 0 && height > 0) {
    const aspect = width / height;

    if (aspect > 1) {
      const visibleWidth = 1 / aspect;

      texture.repeat.set(
        visibleWidth,
        1
      );

      texture.offset.set(
        (1 - visibleWidth) / 2,
        0
      );
    } else if (aspect < 1) {
      const visibleHeight = aspect;

      texture.repeat.set(
        1,
        visibleHeight
      );

      texture.offset.set(
        0,
        (1 - visibleHeight) / 2
      );
    }
  }

  texture.needsUpdate = true;
}

function AvaVisionEye({
  active,
  eyeOpen,
  status,
  captureCanvasFrameRef,
  targetMesh,
}: {
  active: boolean;
  eyeOpen: boolean;
  status: AvaVisionUserStatus;
  captureCanvasFrameRef: {
    current: () => HTMLCanvasElement | null;
  };
  targetMesh?: THREE.Mesh;
}) {
  const eyeGroupRef =
    useRef<THREE.Group>(null);

  const textureRef =
    useRef<THREE.Texture | null>(null);

  const [texture, setTexture] =
    useState<THREE.Texture | null>(null);

  const frameReadyRef =
    useRef(false);


  /*
   * AVAAIO LIQUID SILVER IRIS PHASE 4C
   * The live ring shifts gently through silver, ice blue,
   * and violet instead of remaining one static green.
   */
  const ringMaterialRef =
    useRef<THREE.MeshBasicMaterial>(
      null
    );

  const [frameReady, setFrameReady] =
    useState(false);

  /*
   * Ava's Canvas may use demand-based rendering on mobile.
   * Explicit invalidation guarantees that updated camera
   * pixels are uploaded and painted by WebGL.
   */
  const invalidate =
    useThree(
      (state) => state.invalidate
    );

  /*
   * Cube.027 is Ava's real central eye. Its source geometry has
   * no TEXCOORD_0 data, so the live camera cannot be assigned
   * directly to its original material.
   *
   * Create a UV-enabled circular surface from Cube.027's real
   * local transform and geometry bounds instead of HALO_POS.
   */
  const eyeTarget =
    useMemo(() => {
      const fallbackRadius =
        HALO_RADIUS * 0.91;

      if (
        !targetMesh ||
        !targetMesh.geometry
      ) {
        return {
          position: new THREE.Vector3(
            HALO_POS[0],
            HALO_POS[1],
            HALO_POS[2]
          ),
          quaternion:
            new THREE.Quaternion(),
          scale:
            new THREE.Vector3(1, 1, 1),
          radius: fallbackRadius,
          ringRadius:
            fallbackRadius * 1.06,
        };
      }

      const geometry =
        targetMesh.geometry as
          THREE.BufferGeometry;

      geometry.computeBoundingBox();

      const bounds =
        geometry.boundingBox;

      if (!bounds) {
        return {
          position:
            targetMesh.position.clone(),
          quaternion:
            targetMesh.quaternion.clone(),
          scale:
            targetMesh.scale.clone(),
          radius: fallbackRadius,
          ringRadius:
            fallbackRadius * 1.06,
        };
      }

      const center =
        new THREE.Vector3();

      const size =
        new THREE.Vector3();

      bounds.getCenter(center);
      bounds.getSize(size);

      const localRadius =
        THREE.MathUtils.clamp(
          Math.min(
            Math.abs(size.x),
            Math.abs(size.y)
          ) * 0.49,
          0.035,
          0.21
        );

      /*
       * Cube.027 points toward positive Z. Place the camera
       * surface immediately in front of the original eye.
       */
      const localSurface =
        new THREE.Vector3(
          center.x,
          center.y,
          bounds.max.z +
            Math.max(
              0.003,
              Math.abs(size.z) * 0.08
            )
        );

      const transformedSurface =
        localSurface
          .clone()
          .multiply(
            targetMesh.scale
          )
          .applyQuaternion(
            targetMesh.quaternion
          );

      return {
        position:
          targetMesh.position
            .clone()
            .add(
              transformedSurface
            ),
        quaternion:
          targetMesh.quaternion.clone(),
        scale:
          targetMesh.scale.clone(),
        radius: localRadius,
        ringRadius:
          localRadius * 1.06,
      };
    }, [targetMesh]);

  const originalEyeVisibleRef =
    useRef<boolean | null>(null);

  useEffect(() => {
    if (!targetMesh) {
      return;
    }

    originalEyeVisibleRef.current =
      targetMesh.visible;

    return () => {
      if (
        originalEyeVisibleRef.current !==
        null
      ) {
        targetMesh.visible =
          originalEyeVisibleRef.current;
      }
    };
  }, [targetMesh]);

  useEffect(() => {
    let disposed = false;

    const previousTexture =
      textureRef.current;

    if (previousTexture) {
      previousTexture.dispose();
      textureRef.current = null;
    }

    setTexture(null);

    frameReadyRef.current = false;
    setFrameReady(false);

    if (!active) {
      invalidate();

      return () => {
        disposed = true;
      };
    }

    const displayCanvas =
      document.createElement("canvas");

    displayCanvas.width =
      AVA_VISION_EYE_CANVAS_SIZE;

    displayCanvas.height =
      AVA_VISION_EYE_CANVAS_SIZE;

    const context =
      displayCanvas.getContext(
        "2d",
        {
          alpha: false,
        }
      );

    if (!context) {
      return () => {
        disposed = true;
      };
    }

    context.imageSmoothingEnabled =
      true;

    context.imageSmoothingQuality =
      "high";

    /*
     * Visible Vision-starting texture. This confirms that
     * the eye material and WebGL redraw path are active even
     * before the first camera frame becomes available.
     */
    /*
     * AVAAIO PRO V77 CAMERA ACTIVATION
     *
     * Camera OFF is represented by Ava's physical dark lens.
     * When Vision begins, the overlay first becomes liquid
     * metallic gray before the first live camera frame arrives.
     */
    const activationGradient =
      context.createRadialGradient(
        displayCanvas.width * 0.42,
        displayCanvas.height * 0.36,
        displayCanvas.width * 0.04,
        displayCanvas.width * 0.50,
        displayCanvas.height * 0.50,
        displayCanvas.width * 0.62
      );

    activationGradient.addColorStop(
      0.00,
      "#b8c2c7"
    );

    activationGradient.addColorStop(
      0.24,
      "#727d84"
    );

    activationGradient.addColorStop(
      0.52,
      "#353e45"
    );

    activationGradient.addColorStop(
      0.78,
      "#151b20"
    );

    activationGradient.addColorStop(
      1.00,
      "#05080b"
    );

    context.fillStyle =
      activationGradient;

    context.fillRect(
      0,
      0,
      displayCanvas.width,
      displayCanvas.height
    );

    const activationSheen =
      context.createLinearGradient(
        0,
        0,
        displayCanvas.width,
        displayCanvas.height
      );

    activationSheen.addColorStop(
      0.00,
      "rgba(255,255,255,0.04)"
    );

    activationSheen.addColorStop(
      0.42,
      "rgba(225,238,244,0.30)"
    );

    activationSheen.addColorStop(
      0.58,
      "rgba(100,122,134,0.10)"
    );

    activationSheen.addColorStop(
      1.00,
      "rgba(255,255,255,0.00)"
    );

    context.fillStyle =
      activationSheen;

    context.fillRect(
      0,
      0,
      displayCanvas.width,
      displayCanvas.height
    );

    const canvasTexture =
      new THREE.CanvasTexture(
        displayCanvas
      );

    configureAvaVisionEyeTexture(
      canvasTexture
    );

    canvasTexture.generateMipmaps =
      false;

    canvasTexture.minFilter =
      THREE.LinearFilter;

    canvasTexture.magFilter =
      THREE.LinearFilter;

    canvasTexture.needsUpdate =
      true;

    textureRef.current =
      canvasTexture;

    setTexture(
      canvasTexture
    );

    invalidate();

    const drawCapturedCanvas = () => {
      if (
        disposed ||
        !active
      ) {
        return;
      }

      let sourceCanvas:
        HTMLCanvasElement | null = null;

      try {
        sourceCanvas =
          captureCanvasFrameRef.current?.() ||
          null;
      } catch {
        sourceCanvas = null;
      }

      if (
        !sourceCanvas ||
        sourceCanvas.width < 2 ||
        sourceCanvas.height < 2
      ) {
        return;
      }

      const sourceWidth =
        sourceCanvas.width;

      const sourceHeight =
        sourceCanvas.height;

      const sourceSize =
        Math.min(
          sourceWidth,
          sourceHeight
        );

      const sourceX =
        (sourceWidth - sourceSize) / 2;

      const sourceY =
        (sourceHeight - sourceSize) / 2;

      context.save();

      context.setTransform(
        1,
        0,
        0,
        1,
        0,
        0
      );

      context.fillStyle =
        "#02151e";

      context.fillRect(
        0,
        0,
        displayCanvas.width,
        displayCanvas.height
      );

      /*
       * Mirror only the user-facing eye preview. Ava's
       * Vision analysis continues receiving its original,
       * unmirrored camera frame.
       */
      context.translate(
        displayCanvas.width,
        0
      );

      context.scale(
        -1,
        1
      );

      /*
       * Convert the raw camera preview into a metallic,
       * silver-toned Vision image.
       */
      context.filter =
        "grayscale(0.82) " +
        "saturate(0.28) " +
        "contrast(1.24) " +
        "brightness(0.76)";

      context.drawImage(
        sourceCanvas,
        sourceX,
        sourceY,
        sourceSize,
        sourceSize,
        0,
        0,
        displayCanvas.width,
        displayCanvas.height
      );

      context.filter = "none";

      context.restore();

      /*
       * Animated liquid-metal reflection layer.
       * No pure-white fixed hotspot is used.
       */
      const liquidTime =
        performance.now() * 0.001;

      const liquidWidth =
        displayCanvas.width;

      const liquidHeight =
        displayCanvas.height;

      const sweepPosition =
        0.50 +
        Math.sin(
          liquidTime * 0.78
        ) * 0.30;

      context.save();

      context.globalCompositeOperation =
        "screen";

      const liquidSheen =
        context.createLinearGradient(
          liquidWidth *
            (sweepPosition - 0.40),
          0,
          liquidWidth *
            (sweepPosition + 0.40),
          liquidHeight
        );

      liquidSheen.addColorStop(
        0.00,
        "rgba(20, 35, 45, 0.00)"
      );

      liquidSheen.addColorStop(
        0.28,
        "rgba(35, 50, 60, 0.06)"
      );

      liquidSheen.addColorStop(
        0.48,
        "rgba(60, 78, 88, 0.12)"
      );

      liquidSheen.addColorStop(
        0.58,
        "rgba(35, 70, 85, 0.08)"
      );

      liquidSheen.addColorStop(
        0.72,
        "rgba(45, 40, 60, 0.05)"
      );

      liquidSheen.addColorStop(
        1.00,
        "rgba(20, 35, 45, 0.00)"
      );

      context.fillStyle =
        liquidSheen;

      context.fillRect(
        0,
        0,
        liquidWidth,
        liquidHeight
      );

      const reflectionX =
        liquidWidth *
        (
          0.48 +
          Math.sin(
            liquidTime * 0.56
          ) * 0.12
        );

      const reflectionY =
        liquidHeight *
        (
          0.42 +
          Math.cos(
            liquidTime * 0.68
          ) * 0.10
        );

      const silverReflection =
        context.createRadialGradient(
          reflectionX,
          reflectionY,
          0,
          reflectionX,
          reflectionY,
          liquidWidth * 0.42
        );

      silverReflection.addColorStop(
        0.00,
        "rgba(65, 78, 84, 0.10)"
      );

      silverReflection.addColorStop(
        0.24,
        "rgba(38, 55, 65, 0.07)"
      );

      silverReflection.addColorStop(
        0.58,
        "rgba(25, 42, 52, 0.04)"
      );

      silverReflection.addColorStop(
        1.00,
        "rgba(10, 18, 28, 0.00)"
      );

      context.fillStyle =
        silverReflection;

      context.fillRect(
        0,
        0,
        liquidWidth,
        liquidHeight
      );

      /*
       * Slowly rotating silver arc gives the iris a
       * fluid mechanical movement.
       */
      const arcRotation =
        liquidTime * 0.72;

      context.strokeStyle =
        "rgba(72, 88, 96, 0.10)";

      context.lineWidth =
        Math.max(
          2,
          liquidWidth * 0.018
        );

      context.beginPath();

      context.arc(
        liquidWidth / 2,
        liquidHeight / 2,
        liquidWidth * 0.34,
        arcRotation,
        arcRotation + Math.PI * 0.92
      );

      context.stroke();

      context.globalCompositeOperation =
        "source-over";

      /*
       * Dark metallic edge vignette prevents another
       * hard white center from forming.
       */
      const liquidVignette =
        context.createRadialGradient(
          liquidWidth / 2,
          liquidHeight / 2,
          liquidWidth * 0.16,
          liquidWidth / 2,
          liquidHeight / 2,
          liquidWidth * 0.58
        );

      liquidVignette.addColorStop(
        0.00,
        "rgba(4, 12, 18, 0.02)"
      );

      liquidVignette.addColorStop(
        0.60,
        "rgba(3, 12, 20, 0.10)"
      );

      liquidVignette.addColorStop(
        1.00,
        "rgba(1, 5, 10, 0.52)"
      );

      context.fillStyle =
        liquidVignette;

      context.fillRect(
        0,
        0,
        liquidWidth,
        liquidHeight
      );

      context.restore();

      /*
       * AVAAIO PRO V77 LIVE EYE TEST
       *
       * Preserve the existing Vision capture pipeline and outer
       * status ring, but make the final central-eye pixels the
       * recognizable live camera image instead of allowing the
       * liquid-silver treatment to obscure the user's image.
       */
      context.save();

      context.setTransform(
        1,
        0,
        0,
        1,
        0,
        0
      );

      context.fillStyle =
        "#02070c";

      context.fillRect(
        0,
        0,
        displayCanvas.width,
        displayCanvas.height
      );

      context.translate(
        displayCanvas.width,
        0
      );

      context.scale(
        -1,
        1
      );

      context.filter =
        "contrast(1.06) brightness(0.94) saturate(0.92)";

      context.drawImage(
        sourceCanvas,
        sourceX,
        sourceY,
        sourceSize,
        sourceSize,
        0,
        0,
        displayCanvas.width,
        displayCanvas.height
      );

      context.filter = "none";
      context.restore();

      canvasTexture.needsUpdate =
        true;

      /*
       * Critical for demand-rendered mobile R3F canvases.
       */
      invalidate();

      if (!frameReadyRef.current) {
        frameReadyRef.current = true;
        setFrameReady(true);
      }
    };

    drawCapturedCanvas();

    const timer =
      window.setInterval(
        drawCapturedCanvas,
        AVA_VISION_EYE_CAPTURE_MS
      );

    return () => {
      disposed = true;

      window.clearInterval(
        timer
      );

      if (
        textureRef.current ===
        canvasTexture
      ) {
        textureRef.current = null;
      }

      canvasTexture.dispose();

      invalidate();
    };
  }, [
    active,
    captureCanvasFrameRef,
    invalidate,
  ]);


  /*
   * Preserve Ava's existing blink behavior by smoothly
   * compressing the circular preview vertically.
   */
  useFrame(() => {
    if (
      targetMesh &&
      originalEyeVisibleRef.current !==
        null
    ) {
      /*
       * Hide Cube.027 only while Vision is active. The
       * original emissive eye returns immediately afterward.
       */
      targetMesh.visible =
        active
          ? false
          : originalEyeVisibleRef.current;
    }

    const group =
      eyeGroupRef.current;

    if (!group) return;

    /*
     * Keep the camera lens fully visible while Vision is on.
     * Blink compression remains available outside Vision.
     */
    const targetY =
      active
        ? 1
        : eyeOpen
          ? 1
          : 0.055;

    group.scale.y =
      THREE.MathUtils.lerp(
        group.scale.y,
        targetY,
        active || eyeOpen
          ? 0.48
          : 0.58
      );

    const ringMaterial =
      ringMaterialRef.current;

    if (ringMaterial) {
      /*
       * Preserve the liquid-silver camera surface while using
       * the surrounding ring as a clear Vision health signal.
       */
      /*
       * Vision state remains visible through pulse speed
       * and opacity while the ring follows Ava's theme.
       */
      const ringColor = "#d6f8ff";

      const pulseTime =
        performance.now() * 0.001;

      const pulse =
        0.5 +
        Math.sin(
          pulseTime *
          (
            status === "issue"
              ? 4.8
              : status === "visible"
                ? 2.0
                : 2.8
          )
        ) * 0.5;

      ringMaterial.color.set(
        ringColor
      );

      ringMaterial.opacity =
        status === "issue"
          ? 0.48 + pulse * 0.08
          : status === "visible"
            ? 0.32 + pulse * 0.05
            : 0.28 + pulse * 0.05;
    }
  });

  if (!active) {
    return null;
  }

  return (
    <group
      ref={eyeGroupRef}
      position={eyeTarget.position}
      quaternion={eyeTarget.quaternion}
      scale={eyeTarget.scale}
      renderOrder={1000}
    >
      <mesh renderOrder={1001}>
        <circleGeometry
          args={[
            eyeTarget.radius,
            IOS_GPU_SAFE_MODE ? 36 : 64,
          ]}
        />

        {texture ? (
          <meshBasicMaterial
            map={texture}
            color="#ffffff"
            toneMapped={false}
            transparent={true}
            opacity={1}
            depthTest={false}
            depthWrite={false}
            side={THREE.DoubleSide}
          />
        ) : (
          <meshBasicMaterial
            color="#02151e"
            toneMapped={false}
            transparent={false}
            depthTest={false}
            depthWrite={false}
            side={THREE.DoubleSide}
          />
        )}
      </mesh>

      <mesh
        position={[0, 0, 0.006]}
        renderOrder={1002}
        visible={false}
      >
        <torusGeometry
          args={[
            eyeTarget.ringRadius,
            IOS_GPU_SAFE_MODE
              ? 0.0018
              : 0.0023,
            IOS_GPU_SAFE_MODE ? 10 : 16,
            IOS_GPU_SAFE_MODE ? 40 : 72,
          ]}
        />

        <meshBasicMaterial
          ref={ringMaterialRef}
          color="#d6f8ff"
          transparent
          opacity={0.34}
          toneMapped={false}
          depthTest={false}
          depthWrite={false}
          side={THREE.DoubleSide}
        />
      </mesh>
      {/*
       * Liquid-silver lens uses an unlit CanvasTexture.
       * The old point light was removed to prevent the
       * fixed white hotspot.
       */}
    </group>
  );
}


function AvaBootSequence({
  onSlowLoad,
}: {
  onSlowLoad?: () => void;
}) {
  useEffect(() => {
    const timer = window.setTimeout(() => {
      onSlowLoad?.();
    }, 12000);

    return () => {
      window.clearTimeout(timer);
    };
  }, [onSlowLoad]);

  return null;
}

type AvaRenderRecoveryStatus =
  | "loading"
  | "slow"
  | "ready"
  | "error";

class AvaRenderErrorBoundary extends React.Component<
  {
    children: React.ReactNode;
    onError: () => void;
    resetKey: number;
  },
  {
    hasError: boolean;
  }
> {
  constructor(props: {
    children: React.ReactNode;
    onError: () => void;
    resetKey: number;
  }) {
    super(props);

    this.state = {
      hasError: false,
    };
  }

  static getDerivedStateFromError() {
    return {
      hasError: true,
    };
  }

  componentDidCatch(error: unknown) {
    console.error(
      "[AVA] Renderer recovery boundary caught an error",
      error,
    );

    this.props.onError();
  }

  componentDidUpdate(previousProps: {
    resetKey: number;
  }) {
    if (
      previousProps.resetKey !== this.props.resetKey &&
      this.state.hasError
    ) {
      this.setState({
        hasError: false,
      });
    }
  }

  render() {
    if (this.state.hasError) {
      return null;
    }

    return this.props.children;
  }
}


// ==========================================================
// AVAAIO USER VISION LIVE PREVIEW PHASE 4E
//
// User-facing confirmation of the exact camera frame Ava is
// receiving. This reuses Ava's existing capture canvas.
// It does not create a second camera stream.
// ==========================================================

const AVA_VISION_PREVIEW_WIDTH = 480;
const AVA_VISION_PREVIEW_HEIGHT = 300;


type AvaVisionUserStatus =
  | "connecting"
  | "visible"
  | "searching"
  | "issue";

type AvaVisionPreviewRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

function clampAvaVisionPreview(
  value: number,
  min: number,
  max: number
) {
  return Math.max(
    min,
    Math.min(max, value)
  );
}

function readAvaVisionSafeArea() {
  if (
    typeof document === "undefined" ||
    !document.body
  ) {
    return {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    };
  }

  const probe =
    document.createElement("div");

  probe.setAttribute(
    "aria-hidden",
    "true"
  );

  probe.style.cssText = [
    "position:fixed",
    "visibility:hidden",
    "pointer-events:none",
    "width:0",
    "height:0",
    "padding-top:env(safe-area-inset-top)",
    "padding-right:env(safe-area-inset-right)",
    "padding-bottom:env(safe-area-inset-bottom)",
    "padding-left:env(safe-area-inset-left)",
  ].join(";");

  document.body.appendChild(probe);

  const computed =
    window.getComputedStyle(probe);

  const numberValue = (
    value: string
  ) => {
    const parsed =
      Number.parseFloat(value || "0");

    return Number.isFinite(parsed)
      ? parsed
      : 0;
  };

  const insets = {
    top: numberValue(
      computed.paddingTop
    ),
    right: numberValue(
      computed.paddingRight
    ),
    bottom: numberValue(
      computed.paddingBottom
    ),
    left: numberValue(
      computed.paddingLeft
    ),
  };

  probe.remove();

  return insets;
}

function getAvaVisionPreviewRect():
  AvaVisionPreviewRect {
  if (typeof window === "undefined") {
    return {
      left: 220,
      top: 76,
      width: 142,
      height: 106,
    };
  }

  const visualViewport =
    window.visualViewport;

  const viewportLeft =
    visualViewport?.offsetLeft || 0;

  const viewportTop =
    visualViewport?.offsetTop || 0;

  const viewportWidth =
    visualViewport?.width ||
    window.innerWidth;

  const viewportHeight =
    visualViewport?.height ||
    window.innerHeight;

  const safeArea =
    readAvaVisionSafeArea();

  const landscape =
    viewportWidth > viewportHeight;

  const compactLandscape =
    landscape &&
    viewportWidth <= 932 &&
    viewportHeight <= 520;

  const portraitPhone =
    !landscape &&
    viewportWidth <= 640;

  const tablet =
    viewportWidth <= 1024;

  let cardWidth: number;
  let cardHeight: number;
  let preferredTop: number;
  let margin: number;

  if (compactLandscape) {
    cardWidth =
      clampAvaVisionPreview(
        viewportWidth * 0.22,
        126,
        154
      );

    cardHeight =
      clampAvaVisionPreview(
        cardWidth * 0.70,
        88,
        108
      );

    preferredTop =
      viewportTop +
      Math.max(
        8,
        safeArea.top + 8
      );

    margin = 8;
  } else if (portraitPhone) {
    cardWidth =
      clampAvaVisionPreview(
        viewportWidth * 0.36,
        120,
        142
      );

    cardHeight =
      clampAvaVisionPreview(
        cardWidth * 0.75,
        90,
        106
      );

    preferredTop =
      viewportTop +
      Math.max(
        76,
        safeArea.top + 58
      );

    margin = 9;
  } else if (tablet) {
    cardWidth =
      clampAvaVisionPreview(
        viewportWidth * 0.22,
        164,
        210
      );

    cardHeight =
      clampAvaVisionPreview(
        cardWidth * 0.72,
        120,
        150
      );

    preferredTop =
      landscape
        ? viewportTop +
          Math.max(
            12,
            safeArea.top + 10
          )
        : viewportTop +
          Math.max(
            94,
            safeArea.top + 74
          );

    margin = 12;
  } else {
    cardWidth =
      clampAvaVisionPreview(
        viewportWidth * 0.17,
        184,
        248
      );

    cardHeight =
      clampAvaVisionPreview(
        cardWidth * 0.69,
        132,
        172
      );

    preferredTop =
      viewportTop +
      (
        viewportHeight -
        cardHeight
      ) / 2;

    margin =
      clampAvaVisionPreview(
        viewportWidth * 0.022,
        12,
        30
      );
  }

  cardWidth =
    Math.min(
      cardWidth,
      Math.max(
        96,
        viewportWidth -
          safeArea.left -
          safeArea.right -
          margin * 2
      )
    );

  cardHeight =
    Math.min(
      cardHeight,
      Math.max(
        72,
        viewportHeight -
          safeArea.top -
          safeArea.bottom -
          margin * 2
      )
    );

  const minimumLeft =
    viewportLeft +
    safeArea.left +
    margin;

  const maximumLeft =
    viewportLeft +
    viewportWidth -
    safeArea.right -
    margin -
    cardWidth;

  const minimumTop =
    viewportTop +
    safeArea.top +
    margin;

  const maximumTop =
    viewportTop +
    viewportHeight -
    safeArea.bottom -
    margin -
    cardHeight;

  return {
    left:
      clampAvaVisionPreview(
        maximumLeft,
        minimumLeft,
        Math.max(
          minimumLeft,
          maximumLeft
        )
      ),
    top:
      clampAvaVisionPreview(
        preferredTop,
        minimumTop,
        Math.max(
          minimumTop,
          maximumTop
        )
      ),
    width: cardWidth,
    height: cardHeight,
  };
}

function AvaVisionLivePreview({
  active,
  status,
  captureCanvasFrameRef,
}: {
  active: boolean;
  status: AvaVisionUserStatus;
  captureCanvasFrameRef: {
    current: () => HTMLCanvasElement | null;
  };
}) {
  const previewCanvasRef =
    useRef<HTMLCanvasElement>(null);

  const previewReadyRef =
    useRef(false);

  const [
    previewReady,
    setPreviewReady,
  ] = useState(false);

  /*
   * The preview is rendered through document.body and positioned
   * against the real VisualViewport. It therefore remains inside
   * phone/tablet landscape even when Ava's 3D stage is scaled.
   */
  const [
    previewRect,
    setPreviewRect,
  ] = useState<AvaVisionPreviewRect>(
    getAvaVisionPreviewRect
  );

  const previewCardRef =
    useRef<HTMLElement>(null);

  const previewRectRef =
    useRef(previewRect);

  const previewWasMovedRef =
    useRef(false);

  const previewDragRef = useRef({
    active: false,
    pointerId: -1,
    startX: 0,
    startY: 0,
    startLeft: 0,
    startTop: 0,
    startVisualLeft: 0,
    startVisualTop: 0,
    width: 0,
    height: 0,
  });

  const getPreviewDockTop = () => {
    if (
      typeof document === "undefined" ||
      typeof window === "undefined"
    ) {
      return null;
    }

    const viewport =
      window.visualViewport;

    const viewportTop =
      viewport?.offsetTop || 0;

    const viewportHeight =
      viewport?.height ||
      window.innerHeight;

    const viewportBottom =
      viewportTop +
      viewportHeight;

    const elements =
      Array.from(
        document.querySelectorAll<HTMLElement>(
          ".holo-console, .chat-dock > *"
        )
      );

    let nearestTop:
      number | null = null;

    for (const element of elements) {
      const style =
        window.getComputedStyle(element);

      const rect =
        element.getBoundingClientRect();

      const opacity =
        Number.parseFloat(
          style.opacity || "1"
        );

      const visible =
        style.display !== "none" &&
        style.visibility !== "hidden" &&
        opacity !== 0 &&
        rect.width > 1 &&
        rect.height > 1 &&
        rect.bottom > viewportTop &&
        rect.top < viewportBottom;

      if (!visible) {
        continue;
      }

      const lowerControl =
        rect.top >
          viewportTop +
          Math.min(
            130,
            viewportHeight * 0.30
          ) &&
        rect.bottom >
          viewportTop +
          viewportHeight * 0.52 &&
        rect.height <
          viewportHeight * 0.72;

      if (!lowerControl) {
        continue;
      }

      nearestTop =
        nearestTop === null
          ? rect.top
          : Math.min(
              nearestTop,
              rect.top
            );
    }

    return nearestTop;
  };

  const clampPreviewVisual = (
    candidate: AvaVisionPreviewRect
  ): AvaVisionPreviewRect => {
    if (
      typeof window === "undefined"
    ) {
      return candidate;
    }

    const viewport =
      window.visualViewport;

    const viewportLeft =
      viewport?.offsetLeft || 0;

    const viewportTop =
      viewport?.offsetTop || 0;

    const viewportWidth =
      viewport?.width ||
      window.innerWidth;

    const viewportHeight =
      viewport?.height ||
      window.innerHeight;

    const safeArea =
      readAvaVisionSafeArea();

    const margin = 8;
    const dockGap = 12;

    const minimumLeft =
      viewportLeft +
      safeArea.left +
      margin;

    const maximumLeft =
      Math.max(
        minimumLeft,
        viewportLeft +
          viewportWidth -
          safeArea.right -
          margin -
          candidate.width
      );

    const minimumTop =
      viewportTop +
      safeArea.top +
      margin;

    const viewportMaximumTop =
      viewportTop +
      viewportHeight -
      safeArea.bottom -
      margin -
      candidate.height;

    const dockTop =
      getPreviewDockTop();

    const dockMaximumTop =
      dockTop !== null
        ? dockTop -
          dockGap -
          candidate.height
        : viewportMaximumTop;

    const maximumTop =
      Math.max(
        minimumTop,
        Math.min(
          viewportMaximumTop,
          dockMaximumTop
        )
      );

    return {
      ...candidate,
      left:
        Math.max(
          minimumLeft,
          Math.min(
            maximumLeft,
            candidate.left
          )
        ),
      top:
        Math.max(
          minimumTop,
          Math.min(
            maximumTop,
            candidate.top
          )
        ),
    };
  };

  useEffect(() => {
    previewRectRef.current =
      previewRect;
  }, [previewRect]);

  useEffect(() => {
    if (!active) {
      return;
    }

    let layoutFrame = 0;

    const layoutTimers:
      number[] = [];

    const syncLayout = () => {
      window.cancelAnimationFrame(
        layoutFrame
      );

      layoutFrame =
        window.requestAnimationFrame(
          () => {
            const freshDefault =
              getAvaVisionPreviewRect();

            if (
              !previewWasMovedRef.current
            ) {
              previewRectRef.current =
                freshDefault;

              setPreviewRect(
                freshDefault
              );

              return;
            }

            const current =
              previewRectRef.current;

            const element =
              previewCardRef.current;

            const measured =
              element?.getBoundingClientRect();

            if (!measured) {
              return;
            }

            const bounded =
              clampPreviewVisual({
                left: measured.left,
                top: measured.top,
                width: freshDefault.width,
                height: freshDefault.height,
              });

            /*
             * Preserve the card's real visual location even
             * though its CSS uses translateY(-50%).
             */
            const verticalRatio =
              measured.height > 0
                ? (
                    measured.top -
                    current.top
                  ) / measured.height
                : -0.5;

            const horizontalRatio =
              measured.width > 0
                ? (
                    measured.left -
                    current.left
                  ) / measured.width
                : 0;

            const next = {
              left:
                bounded.left -
                horizontalRatio *
                  freshDefault.width,

              top:
                bounded.top -
                verticalRatio *
                  freshDefault.height,

              width:
                freshDefault.width,

              height:
                freshDefault.height,
            };

            previewRectRef.current =
              next;

            setPreviewRect(
              next
            );
          }
        );
    };

    syncLayout();

    [80, 220, 500, 900].forEach(
      delay => {
        layoutTimers.push(
          window.setTimeout(
            syncLayout,
            delay
          )
        );
      }
    );

    window.addEventListener(
      "resize",
      syncLayout
    );

    window.addEventListener(
      "orientationchange",
      syncLayout
    );

    window.visualViewport
      ?.addEventListener(
        "resize",
        syncLayout
      );

    window.visualViewport
      ?.addEventListener(
        "scroll",
        syncLayout
      );

    return () => {
      window.cancelAnimationFrame(
        layoutFrame
      );

      layoutTimers.forEach(
        timer =>
          window.clearTimeout(timer)
      );

      window.removeEventListener(
        "resize",
        syncLayout
      );

      window.removeEventListener(
        "orientationchange",
        syncLayout
      );

      window.visualViewport
        ?.removeEventListener(
          "resize",
          syncLayout
        );

      window.visualViewport
        ?.removeEventListener(
          "scroll",
          syncLayout
        );
    };
  }, [active]);

  useEffect(() => {
    let disposed = false;
    let animationFrame = 0;
    let lastDrawAt = 0;

    previewReadyRef.current = false;
    setPreviewReady(false);

    const previewCanvas =
      previewCanvasRef.current;

    if (!active || !previewCanvas) {
      return () => {
        disposed = true;
      };
    }

    previewCanvas.width =
      AVA_VISION_PREVIEW_WIDTH;

    previewCanvas.height =
      AVA_VISION_PREVIEW_HEIGHT;

    const context =
      previewCanvas.getContext(
        "2d",
        {
          alpha: false,
        }
      );

    if (!context) {
      return () => {
        disposed = true;
      };
    }

    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";

    context.fillStyle = "#02080d";

    context.fillRect(
      0,
      0,
      previewCanvas.width,
      previewCanvas.height
    );

    /*
     * Maintain a modest preview frame rate so the user can
     * confirm Ava's view without adding heavy mobile GPU load.
     */
    const frameInterval =
      IOS_GPU_SAFE_MODE
        ? 150
        : 95;

    const drawPreview = (
      now: number
    ) => {
      if (disposed) {
        return;
      }

      if (
        now - lastDrawAt >=
        frameInterval
      ) {
        lastDrawAt = now;

        let sourceCanvas:
          HTMLCanvasElement | null = null;

        try {
          sourceCanvas =
            captureCanvasFrameRef.current?.() ||
            null;
        } catch {
          sourceCanvas = null;
        }

        if (
          sourceCanvas &&
          sourceCanvas.width >= 2 &&
          sourceCanvas.height >= 2
        ) {
          const targetWidth =
            previewCanvas.width;

          const targetHeight =
            previewCanvas.height;

          const sourceWidth =
            sourceCanvas.width;

          const sourceHeight =
            sourceCanvas.height;

          /*
           * Preserve the complete camera frame. Empty space
           * becomes a subtle dark letterbox instead of cropping
           * objects out of what the user believes Ava can see.
           */
          const scale =
            Math.min(
              targetWidth / sourceWidth,
              targetHeight / sourceHeight
            );

          const drawWidth =
            sourceWidth * scale;

          const drawHeight =
            sourceHeight * scale;

          const drawX =
            (
              targetWidth -
              drawWidth
            ) / 2;

          const drawY =
            (
              targetHeight -
              drawHeight
            ) / 2;

          context.save();

          context.setTransform(
            1,
            0,
            0,
            1,
            0,
            0
          );

          context.fillStyle =
            "#02080d";

          context.fillRect(
            0,
            0,
            targetWidth,
            targetHeight
          );

          /*
           * Mirror the user-facing preview like a selfie view.
           * The actual Vision analysis canvas is unchanged.
           */
          context.translate(
            targetWidth,
            0
          );

          context.scale(
            -1,
            1
          );

          context.drawImage(
            sourceCanvas,
            0,
            0,
            sourceWidth,
            sourceHeight,
            drawX,
            drawY,
            drawWidth,
            drawHeight
          );

          context.restore();

          /*
           * Subtle readability veil only. This does not change
           * the frame Ava analyzes.
           */
          const veil =
            context.createLinearGradient(
              0,
              0,
              0,
              targetHeight
            );

          veil.addColorStop(
            0,
            "rgba(0, 8, 14, 0.15)"
          );

          veil.addColorStop(
            0.48,
            "rgba(0, 8, 14, 0.00)"
          );

          veil.addColorStop(
            1,
            "rgba(0, 8, 14, 0.18)"
          );

          context.fillStyle = veil;

          context.fillRect(
            0,
            0,
            targetWidth,
            targetHeight
          );

          if (
            !previewReadyRef.current
          ) {
            previewReadyRef.current =
              true;

            setPreviewReady(true);
          }
        }
      }

      animationFrame =
        window.requestAnimationFrame(
          drawPreview
        );
    };

    animationFrame =
      window.requestAnimationFrame(
        drawPreview
      );

    return () => {
      disposed = true;

      window.cancelAnimationFrame(
        animationFrame
      );
    };
  }, [
    active,
    captureCanvasFrameRef,
  ]);

  const startPreviewDrag = (
    event:
      import("react").PointerEvent<HTMLElement>
  ) => {
    if (
      event.pointerType === "mouse" &&
      event.button !== 0
    ) {
      return;
    }

    const element =
      previewCardRef.current;

    if (!element) {
      return;
    }

    const measured =
      element.getBoundingClientRect();

    const current =
      previewRectRef.current;

    previewDragRef.current = {
      active: true,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startLeft: current.left,
      startTop: current.top,
      startVisualLeft: measured.left,
      startVisualTop: measured.top,
      width: measured.width,
      height: measured.height,
    };

    try {
      event.currentTarget
        .setPointerCapture(
          event.pointerId
        );
    } catch {}

    event.preventDefault();
  };

  const movePreviewDrag = (
    event:
      import("react").PointerEvent<HTMLElement>
  ) => {
    const drag =
      previewDragRef.current;

    if (
      !drag.active ||
      drag.pointerId !==
        event.pointerId
    ) {
      return;
    }

    const deltaX =
      event.clientX -
      drag.startX;

    const deltaY =
      event.clientY -
      drag.startY;

    if (
      Math.abs(deltaX) +
        Math.abs(deltaY) >
      2
    ) {
      previewWasMovedRef.current =
        true;
    }

    const bounded =
      clampPreviewVisual({
        left:
          drag.startVisualLeft +
          deltaX,
        top:
          drag.startVisualTop +
          deltaY,
        width:
          drag.width,
        height:
          drag.height,
      });

    const current =
      previewRectRef.current;

    const next = {
      left:
        drag.startLeft +
        (
          bounded.left -
          drag.startVisualLeft
        ),
      top:
        drag.startTop +
        (
          bounded.top -
          drag.startVisualTop
        ),
      width:
        current.width,
      height:
        current.height,
    };

    previewRectRef.current =
      next;

    setPreviewRect(
      next
    );

    event.preventDefault();
  };

  const finishPreviewDrag = (
    event:
      import("react").PointerEvent<HTMLElement>
  ) => {
    const drag =
      previewDragRef.current;

    if (
      !drag.active ||
      drag.pointerId !==
        event.pointerId
    ) {
      return;
    }

    previewDragRef.current.active =
      false;

    try {
      if (
        event.currentTarget
          .hasPointerCapture(
            event.pointerId
          )
      ) {
        event.currentTarget
          .releasePointerCapture(
            event.pointerId
          );
      }
    } catch {}

    event.preventDefault();
  };

  if (!active) {
    return null;
  }

  const effectiveStatus:
    AvaVisionUserStatus =
      status === "issue"
        ? "issue"
        : !previewReady
          ? "connecting"
          : status;

  const statusLabel =
    effectiveStatus === "issue"
      ? "ISSUE"
      : effectiveStatus === "visible"
        ? "VISIBLE"
        : effectiveStatus === "searching"
          ? "SEARCHING"
          : "CONNECTING";

  if (
    !active ||
    typeof document === "undefined"
  ) {
    return null;
  }

  const previewCard = (
    <aside
      ref={previewCardRef}
      className="ava-vision-live-preview"
      data-status={effectiveStatus}
      aria-label={
        `Ava Vision live preview: ${statusLabel.toLowerCase()}`
      }
      style={{
        left: `${previewRect.left}px`,
        top: `${previewRect.top}px`,
        width: `${previewRect.width}px`,
        height: `${previewRect.height}px`,
      }}
    >
      <header
        className="ava-vision-live-preview__header"
        onPointerDown={startPreviewDrag}
        onPointerMove={movePreviewDrag}
        onPointerUp={finishPreviewDrag}
        onPointerCancel={finishPreviewDrag}
        onLostPointerCapture={finishPreviewDrag}
        title="Drag to move"
      >
        <span className="ava-vision-live-preview__title">
          AVA VISION
        </span>

        <span className="ava-vision-live-preview__status">
          <span
            className="ava-vision-live-preview__status-dot"
            aria-hidden="true"
          />

          {statusLabel}
        </span>
      </header>

      <div className="ava-vision-live-preview__viewport">
        <canvas
          ref={previewCanvasRef}
          className="ava-vision-live-preview__canvas"
          aria-hidden="true"
        />

        {!previewReady && (
          <div className="ava-vision-live-preview__starting">
            CONNECTING CAMERA
          </div>
        )}

        {effectiveStatus === "issue" && (
          <div className="ava-vision-live-preview__issue">
            CAMERA ISSUE
          </div>
        )}

        <span
          className="ava-vision-live-preview__corner ava-vision-live-preview__corner--tl"
          aria-hidden="true"
        />

        <span
          className="ava-vision-live-preview__corner ava-vision-live-preview__corner--tr"
          aria-hidden="true"
        />

        <span
          className="ava-vision-live-preview__corner ava-vision-live-preview__corner--bl"
          aria-hidden="true"
        />

        <span
          className="ava-vision-live-preview__corner ava-vision-live-preview__corner--br"
          aria-hidden="true"
        />
      </div>

      <footer className="ava-vision-live-preview__footer">
        LIVE MIRRORED VIEW
      </footer>
    </aside>
  );

  return createPortal(
    previewCard,
    document.body
  );
}


// =====================================================
// AVA_IPAD_RAW_RENDER_V7
//
// Diagnostic iPad renderer:
// - loads the existing AVA_REVISED.glb
// - renders only the raw Three.js scene
// - does NOT mount AvaModel
// - does NOT run AvaModel runtime systems
// =====================================================
function IpadRawAvaRenderV7({
  scale,
  position,
  onReady,
  ready,
  accentColor,
}: {
  scale: number;
  position: [number, number, number];
  onReady: () => void;
  ready: boolean;
  accentColor: string;
}) {
  const { scene } = useGLTF(
    "/models/AVA_REVISED.glb"
  ) as any;

  // =====================================================
  // AVA_IPAD_STATIC_VISUAL_RESTORE_V9A
  //
  // Restore Ava's recognizable static visual character
  // without re-enabling the full AvaModel runtime.
  //
  // SAFETY:
  // - no material replacement
  // - no texture/map assignment
  // - no needsUpdate
  // - no transparent/toneMapped/side shader toggles
  // - no mouth animation
  // - no eye animation
  // =====================================================
  useEffect(() => {
    if (!scene) return;

    const accent = new THREE.Color(accentColor);

    const applyMaterial = (mat: any) => {
      if (!mat) return;

      const rawName = String(mat.name || "");
      const name = rawName.toLowerCase();

      // Preserve the existing GLB glass/lens shader completely.
      if (
        rawName === LENS_MATERIAL_NAME ||
        name.includes("glass") ||
        name.includes("lens")
      ) {
        return;
      }

      // Preserve mouth material structure for now.
      if (name.includes("mouth")) {
        return;
      }

      // Ava's white armor/panel identity.
      if (rawName === "WHITE MATERIAL") {
        if (mat.color?.set) {
          mat.color.set(PANEL_WHITE);
        }

        if (typeof mat.metalness === "number") {
          mat.metalness = 0.10;
        }

        if (typeof mat.roughness === "number") {
          mat.roughness = 0.72;
        }

        return;
      }
  const isProCarbonEyeShell =
    name === "pro_v77_eye_shell_lower_carbon" ||
    name === "pro_v77_eye_shell_upper_carbon";

  // Preserve the Pro carbon eye housing exactly as authored in the GLB.
  if (isProCarbonEyeShell) {
    return;
  }

  const isEye =
    name.includes("eye") ||
    name.includes("iris") ||
    name.includes("pupil") ||
    name.includes("orb");

      const isAccentDetail =
        isEye ||
        name.includes("horn") ||
        name.includes("emissive") ||
        name.includes("circuit") ||
        name.includes("strip") ||
        name.includes("glow") ||
        name.includes("propulsion");

      if (isAccentDetail) {
        if (mat.color?.copy) {
          mat.color.copy(accent);
        }

        if (mat.emissive?.copy) {
          mat.emissive.copy(accent);
        }

        if (
          typeof mat.emissiveIntensity === "number"
        ) {
          mat.emissiveIntensity =
            isEye ? 4.5 : 2.4;
        }

        if (
          typeof mat.metalness === "number" &&
          isEye
        ) {
          mat.metalness = 0.0;
        }

        if (
          typeof mat.roughness === "number" &&
          isEye
        ) {
          mat.roughness = 0.08;
        }

        return;
      }

      // Restore the familiar light gunmetal body treatment,
      // but only to plain materials that are not carrying a
      // texture/emissive map from the GLB.
      if (
        !mat.map &&
        !mat.emissiveMap &&
        mat.color?.set
      ) {
        mat.color.set(BASE_GUNMETAL);

        if (typeof mat.metalness === "number") {
          mat.metalness = NON_LENS_METALNESS;
        }

        if (typeof mat.roughness === "number") {
          mat.roughness = NON_LENS_ROUGHNESS;
        }
      }
    };

    let meshCount = 0;
    let materialCount = 0;

    scene.traverse((obj: any) => {
      if (!obj?.isMesh || !obj.material) {
        return;
      }

      meshCount += 1;

      const list = Array.isArray(obj.material)
        ? obj.material
        : [obj.material];

      list.forEach((mat: any) => {
        materialCount += 1;
        applyMaterial(mat);
      });
    });

    console.log(
      "[AVA] V9A static iPad appearance applied",
      {
        meshCount,
        materialCount,
      }
    );
  }, [scene, accentColor]);

  // =====================================================
  // AVA_IPAD_HORN_PROPORTION_V9A3
  //
  // Slightly widen and lengthen the existing GLB horns.
  // Geometry/materials/textures are not replaced.
  //
  // Width:  +35%
  // Height: +55%
  // Raised:  +0.12
  //
  // Base scale is stored once so React remounts/theme
  // changes cannot compound the scaling.
  // =====================================================
  useEffect(() => {
    if (!scene) return;

    let hornMeshCount = 0;

    scene.traverse((obj: any) => {
      if (!obj?.isMesh) return;

      const meshName =
        String(obj.name || "").toLowerCase();

      const materials =
        Array.isArray(obj.material)
          ? obj.material
          : [obj.material];

      const materialHorn =
        materials.some((mat: any) =>
          String(mat?.name || "")
            .toLowerCase()
            .includes("horn")
        );

      const isHorn =
        meshName.includes("horn") ||
        materialHorn;

      if (!isHorn) return;

      obj.userData =
        obj.userData || {};

      if (
        !obj.userData.__avaaioV9A3HornBaseScale
      ) {
        obj.userData.__avaaioV9A3HornBaseScale = {
          x: obj.scale.x,
          y: obj.scale.y,
          z: obj.scale.z,
        };
      }

      const base =
        obj.userData.__avaaioV9A3HornBaseScale;

      // AVA_IPAD_BIG_HIGH_HORNS_V9A4
      //
      // Make the horns visibly wider and substantially taller.
      obj.scale.set(
        base.x * 1.35,
        base.y * 1.55,
        base.z
      );

      // Store the original horn position once.
      if (
        !obj.userData.__avaaioV9A4HornBasePosition
      ) {
        obj.userData.__avaaioV9A4HornBasePosition = {
          x: obj.position.x,
          y: obj.position.y,
          z: obj.position.z,
        };
      }

      const basePosition =
        obj.userData.__avaaioV9A4HornBasePosition;

      // Raise the horns above the head instead of only
      // stretching them inside their original footprint.
      obj.position.set(
        basePosition.x,
        basePosition.y + 0.12,
        basePosition.z
      );

      hornMeshCount += 1;
    });

    console.log(
      "[AVA] V9A.3 horn proportions applied",
      { hornMeshCount }
    );
  }, [scene]);

  // =====================================================
  // AVA_IPAD_EYE_SPECULAR_DOT_CLEANUP_V9A1
  //
  // Keep the V9A eye/lens appearance unchanged except for
  // reducing tiny white specular reflections on the center lens.
  //
  // Existing GLB material only:
  // - no replacement
  // - no texture changes
  // - no needsUpdate
  // =====================================================
  useEffect(() => {
    if (!scene) return;

    let lensMaterialsAdjusted = 0;

    scene.traverse((obj: any) => {
      if (!obj?.isMesh || !obj.material) return;

      const materials =
        Array.isArray(obj.material)
          ? obj.material
          : [obj.material];

      materials.forEach((mat: any) => {
        if (!mat) return;

        const matName =
          String(mat.name || "");

        const lower =
          matName.toLowerCase();

        const isLens =
          matName === LENS_MATERIAL_NAME ||
          lower.includes("glass") ||
          lower.includes("lens");

        if (!isLens) return;

        // Uniform-level highlight suppression only.
        if (
          typeof mat.specularIntensity === "number"
        ) {
          mat.specularIntensity = 0;
        }

        if (
          typeof mat.reflectivity === "number"
        ) {
          mat.reflectivity = 0;
        }

        if (
          typeof mat.envMapIntensity === "number"
        ) {
          mat.envMapIntensity = 0;
        }

        // A modest roughness increase softens any remaining
        // direct-light hotspot without flattening the lens.
        if (
          typeof mat.roughness === "number"
        ) {
          mat.roughness =
            Math.max(mat.roughness, 0.42);
        }

        lensMaterialsAdjusted += 1;
      });
    });

    console.log(
      "[AVA] V9A.1 lens white-dot cleanup applied",
      { lensMaterialsAdjusted }
    );
  }, [scene]);

  useEffect(() => {
    if (!scene) return;

    console.log(
      "[AVA] iPad V7 raw GLB scene mounted"
    );

    const frame = window.requestAnimationFrame(() => {
      // AVA_IPAD_V7_1_DIRECT_READY
      //
      // Tell the owning AvaFace instance directly that the raw
      // diagnostic scene is ready. Keep the global event as a
      // compatibility signal for existing renderer listeners.
      onReady();

      window.dispatchEvent(
        new CustomEvent("ava-render-ready", {
          detail: {
            diagnostic: "ipad-raw-render-v7-1",
          },
        })
      );
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [scene]);

  // AVA_IPAD_V7_2_FRAME_CONFIRMED_READY
  //
  // The raw scene is already loaded at this point. If the owning
  // AvaFace state is still loading/slow while R3F is successfully
  // producing frames, re-announce readiness from the render loop.
  //
  // This removes the one-shot navigation race without using a
  // blind timeout or refreshing the whole page.
  useFrame(() => {
    if (!ready) {
      onReady();
    }
  });

  if (!scene) return null;

  return (
    <primitive
      object={scene}
      scale={scale}
      position={position}
    />
  );
}


export default function AvaFace({
  isPro = false,
  isAdmin = false,
  speaking = false,
  mouthLevel = 0,
  zoomTrigger = 0,
  expression = "NEUTRAL",
  avaEyeOpen,
  compactViewport = false,
}: {
  speaking?: boolean;
  mouthLevel?: number;
  zoomTrigger?: number;
  expression?: string;
  avaEyeOpen?: boolean;
  compactViewport?: boolean;
  isPro?: boolean;
  isAdmin?: boolean;
}) {
  const viewport = useViewportProfile();

  const [avaRenderKey, setAvaRenderKey] =
    useState(0);

  const [avaRenderStatus, setAvaRenderStatus] =
    useState<AvaRenderRecoveryStatus>("loading");

  // =====================================================
  // AVA_IPAD_CANVAS_SELF_RECOVERY_V8
  //
  // Safari/iPad can occasionally return to Dashboard with
  // the existing R3F Canvas no longer producing frames.
  //
  // Recovery policy:
  // - iPad only
  // - wait for normal startup first
  // - remount Canvas exactly once
  // - do NOT reload the page
  // - do NOT clear the GLB cache
  // - if recovery still does not produce readiness,
  //   expose the existing Retry Ava error state
  // =====================================================
  const avaAutoRecoveryAttemptedRef = useRef(false);

  useEffect(() => {
    if (!IOS_GPU_SAFE_MODE) return;

    if (avaRenderStatus === "ready") {
      return;
    }

    if (avaRenderStatus === "error") {
      return;
    }

    const timer = window.setTimeout(() => {
      if (avaAutoRecoveryAttemptedRef.current) {
        console.warn(
          "[AVA] V8 Canvas recovery already attempted; exposing retry state"
        );

        setAvaRenderStatus("error");
        return;
      }

      avaAutoRecoveryAttemptedRef.current = true;

      console.warn(
        "[AVA] V8 iPad Canvas stalled; performing one internal remount"
      );

      setAvaRenderStatus("loading");
      setAvaRenderKey((current) => current + 1);
    }, 7000);

    return () => {
      window.clearTimeout(timer);
    };
  }, [avaRenderStatus]);

  const retryAvaRenderer = () => {
    try {
      (useGLTF as any).clear?.(
        "/models/AVA_REVISED.glb"
      );
    } catch (error) {
      console.warn(
        "[AVA] Model cache clear was unavailable",
        error,
      );
    }

    avaAutoRecoveryAttemptedRef.current = false;
    setAvaRenderStatus("loading");
    setAvaRenderKey((current) => current + 1);
  };

  useEffect(() => {
    const markAvaReady = () => {
      setAvaRenderStatus("ready");
    };

    window.addEventListener(
      "ava-render-ready",
      markAvaReady,
    );

    return () => {
      window.removeEventListener(
        "ava-render-ready",
        markAvaReady,
      );
    };
  }, []);

  const {
    motionTrackingAllowed,
  } = useMotionTrackingEntitlement();

  // =====================================================
  // AVA DEVICE MOTION LOOK
  //
  // iPhone/iPad orientation tracking is opt-in and is
  // activated from the AVA MENU. The current device
  // direction becomes the neutral center when enabled.
  //
  // Tracking authority:
  //   1. Ava Eye / camera face tracking
  //   2. Device motion look
  //   3. Pointer / touch look
  // =====================================================

  // Motion always starts locked until live backend entitlement
  // has been confirmed during this authenticated session.
  const [deviceMotionEnabled, setDeviceMotionEnabled] =
    useState<boolean>(false);

  const [deviceMotionLook, setDeviceMotionLook] = useState({
    x: 0,
    y: 0,
  });

  const deviceMotionBaselineRef = useRef<{
    alpha: number | null;
    beta: number;
    gamma: number;
  } | null>(null);

  const deviceMotionFilteredRef = useRef({
    x: 0,
    y: 0,
  });

  useEffect(() => {
    if (motionTrackingAllowed) return;

    clearMotionTrackingSession();

    deviceMotionBaselineRef.current = null;
    deviceMotionFilteredRef.current = {
      x: 0,
      y: 0,
    };

    setDeviceMotionLook({
      x: 0,
      y: 0,
    });

    setDeviceMotionEnabled(false);
  }, [motionTrackingAllowed]);

  useEffect(() => {
    const onDeviceMotionToggle = (event: Event) => {
      const detail = (
        event as CustomEvent<{
          enabled?: boolean;
          recalibrate?: boolean;
        }>
      ).detail;

      const enabled = Boolean(detail?.enabled);

      if (enabled && !motionTrackingAllowed) {
        console.warn(
          "[AvaFace] Ava Motion Track blocked: paid plan or admin required."
        );

        clearMotionTrackingSession();
        setDeviceMotionEnabled(false);

        return;
      }

      try {
        sessionStorage.setItem(
          "ava_device_motion_look",
          enabled ? "1" : "0"
        );
      } catch {
        // Session storage may be unavailable in private browsing.
      }

      deviceMotionBaselineRef.current = null;
      deviceMotionFilteredRef.current = {
        x: 0,
        y: 0,
      };

      setDeviceMotionLook({
        x: 0,
        y: 0,
      });

      setDeviceMotionEnabled(enabled);
    };

    window.addEventListener(
      "ava-device-motion-toggle",
      onDeviceMotionToggle as EventListener
    );

    return () => {
      window.removeEventListener(
        "ava-device-motion-toggle",
        onDeviceMotionToggle as EventListener
      );
    };
  }, [motionTrackingAllowed]);

  useEffect(() => {
    if (!motionTrackingAllowed || !deviceMotionEnabled) {
      deviceMotionBaselineRef.current = null;
      deviceMotionFilteredRef.current = {
        x: 0,
        y: 0,
      };

      setDeviceMotionLook({
        x: 0,
        y: 0,
      });

      return;
    }

    const canUseDeviceOrientation =
      typeof window !== "undefined" &&
      "DeviceOrientationEvent" in window;

    if (!canUseDeviceOrientation) return;

    let lastUpdateAt = 0;
    let stableHeadingDelta = 0;

    // __AVA_MOTION_IDLE_RECENTER__
    // Ava follows active device movement, then gently returns
    // her gaze toward the user after the device becomes still.
    type MotionOrientationSample = {
      alpha: number | null;
      beta: number;
      gamma: number;
    };

    let latestOrientation:
      MotionOrientationSample | null = null;

    let previousOrientation:
      MotionOrientationSample | null = null;

    let lastMeaningfulMotionAt =
      performance.now();

    let recenterFrame = 0;

    // Short pause prevents Ava from returning during a normal
    // continuous device turn.
    const MOTION_SETTLE_DELAY_MS = 650;

    // Slow return rates preserve awareness instead of creating
    // an artificial snap-to-center effect.
    const MOTION_RETURN_RATE_X = 0.018;
    const MOTION_RETURN_RATE_Y = 0.015;

    const shortestAngleDelta = (
      value: number,
      baseline: number
    ) => {
      return ((value - baseline + 540) % 360) - 180;
    };

    const applyDeadZone = (
      value: number,
      deadZone = 0.035
    ) => {
      if (Math.abs(value) <= deadZone) return 0;

      return (
        Math.sign(value) *
        ((Math.abs(value) - deadZone) / (1 - deadZone))
      );
    };

    const approachWithSpeedLimit = (
      current: number,
      target: number,
      response: number,
      maximumStep: number
    ) => {
      const requestedStep =
        (target - current) * response;

      return (
        current +
        THREE.MathUtils.clamp(
          requestedStep,
          -maximumStep,
          maximumStep
        )
      );
    };

    const resetOrientationCenter = () => {
      deviceMotionBaselineRef.current = null;
      stableHeadingDelta = 0;
      latestOrientation = null;
      previousOrientation = null;
      lastMeaningfulMotionAt =
        performance.now();

      deviceMotionFilteredRef.current = {
        x: 0,
        y: 0,
      };

      setDeviceMotionLook({
        x: 0,
        y: 0,
      });
    };

    const onDeviceOrientation = (
      event: DeviceOrientationEvent
    ) => {
      const now = performance.now();

      // About 30 FPS is enough for awareness while avoiding
      // twitching from high-frequency sensor noise.
      if (now - lastUpdateAt < 32) return;

      const elapsedMs =
        lastUpdateAt > 0
          ? now - lastUpdateAt
          : 32;

      lastUpdateAt = now;

      if (
        typeof event.beta !== "number" ||
        typeof event.gamma !== "number"
      ) {
        return;
      }

      const appleEvent =
        event as DeviceOrientationEvent & {
          webkitCompassHeading?: number;
        };

      const alpha =
        typeof appleEvent.webkitCompassHeading === "number"
          ? appleEvent.webkitCompassHeading
          : typeof event.alpha === "number"
            ? event.alpha
            : null;

      const beta = event.beta;
      const gamma = event.gamma;

      const currentOrientation:
        MotionOrientationSample = {
          alpha,
          beta,
          gamma,
        };

      latestOrientation =
        currentOrientation;

      if (previousOrientation) {
        const headingMovement =
          alpha !== null &&
          previousOrientation.alpha !== null
            ? Math.abs(
                shortestAngleDelta(
                  alpha,
                  previousOrientation.alpha
                )
              )
            : 0;

        const betaMovement =
          Math.abs(
            beta - previousOrientation.beta
          );

        const gammaMovement =
          Math.abs(
            gamma - previousOrientation.gamma
          );

        // Ignore normal sensor vibration. Only meaningful device
        // movement delays Ava's return to the centered user gaze.
        if (
          headingMovement >= 1.75 ||
          betaMovement >= 0.75 ||
          gammaMovement >= 0.75
        ) {
          lastMeaningfulMotionAt = now;
        }
      } else {
        lastMeaningfulMotionAt = now;
      }

      previousOrientation =
        currentOrientation;

      if (!deviceMotionBaselineRef.current) {
        deviceMotionBaselineRef.current = {
          alpha,
          beta,
          gamma,
        };

        stableHeadingDelta = 0;
        lastMeaningfulMotionAt = now;
        return;
      }

      const baseline =
        deviceMotionBaselineRef.current;

      const betaDelta =
        beta - baseline.beta;

      const gammaDelta =
        gamma - baseline.gamma;

      const rawScreenAngle =
        typeof screen !== "undefined" &&
        typeof screen.orientation?.angle === "number"
          ? screen.orientation.angle
          : Number((window as any).orientation || 0);

      const screenAngle =
        ((rawScreenAngle % 360) + 360) % 360;

      // Screen-aware tilt fallback.
      let horizontalTiltDelta = gammaDelta;
      let verticalDelta = betaDelta;

      if (screenAngle === 90) {
        horizontalTiltDelta = betaDelta;
        verticalDelta = -gammaDelta;
      } else if (screenAngle === 180) {
        horizontalTiltDelta = -gammaDelta;
        verticalDelta = -betaDelta;
      } else if (screenAngle === 270) {
        horizontalTiltDelta = -betaDelta;
        verticalDelta = gammaDelta;
      }

      let horizontalDelta =
        horizontalTiltDelta;

      // Heading is useful for true left/right pointing, but it
      // is stabilized before use. Magnetic jumps are rejected
      // and real movement is rate-limited.
      if (
        alpha !== null &&
        baseline.alpha !== null
      ) {
        const headingCandidate =
          shortestAngleDelta(
            alpha,
            baseline.alpha
          );

        const headingDifference =
          headingCandidate -
          stableHeadingDelta;

        const maximumHeadingStep =
          THREE.MathUtils.clamp(
            elapsedMs * 0.22,
            5,
            12
          );

        // Ignore impossible magnetic jumps. Real device turns
        // continue moving toward the candidate over later frames.
        if (Math.abs(headingDifference) < 70) {
          stableHeadingDelta +=
            THREE.MathUtils.clamp(
              headingDifference,
              -maximumHeadingStep,
              maximumHeadingStep
            );
        }

        horizontalDelta =
          stableHeadingDelta;
      }

      // Positive horizontal input follows the same convention
      // used by pointer tracking. This removes the old double
      // inversion that made Ava move opposite the phone.
      let targetX = THREE.MathUtils.clamp(
        horizontalDelta / 58,
        -0.34,
        0.34
      );

      // Up/down remains relative to the activation position.
      // The range is intentionally smaller so Ava stays engaged
      // with the person rather than staring away.
      let targetY = THREE.MathUtils.clamp(
        -verticalDelta / 48,
        -0.25,
        0.25
      );

      targetX = applyDeadZone(targetX);
      targetY = applyDeadZone(targetY);

      const filtered =
        deviceMotionFilteredRef.current;

      // Two-stage response: smooth easing plus a strict maximum
      // per-frame movement. Fast phone movements therefore
      // produce natural head lag instead of snapping.
      filtered.x = approachWithSpeedLimit(
        filtered.x,
        targetX,
        0.11,
        0.016
      );

      filtered.y = approachWithSpeedLimit(
        filtered.y,
        targetY,
        0.10,
        0.013
      );

      setDeviceMotionLook((previous) => {
        if (
          Math.abs(
            previous.x - filtered.x
          ) < 0.0008 &&
          Math.abs(
            previous.y - filtered.y
          ) < 0.0008
        ) {
          return previous;
        }

        return {
          x: filtered.x,
          y: filtered.y,
        };
      });
    };

    const recenterWhenStill = () => {
      const now = performance.now();

      const stillFor =
        now - lastMeaningfulMotionAt;

      if (
        stillFor >= MOTION_SETTLE_DELAY_MS
      ) {
        // Rebase around the stationary phone position. This
        // allows Ava to look back at the user while preserving
        // awareness of the next movement from this new angle.
        if (latestOrientation) {
          deviceMotionBaselineRef.current = {
            alpha: latestOrientation.alpha,
            beta: latestOrientation.beta,
            gamma: latestOrientation.gamma,
          };

          stableHeadingDelta = 0;
        }

        const filtered =
          deviceMotionFilteredRef.current;

        let nextX =
          THREE.MathUtils.lerp(
            filtered.x,
            0,
            MOTION_RETURN_RATE_X
          );

        let nextY =
          THREE.MathUtils.lerp(
            filtered.y,
            0,
            MOTION_RETURN_RATE_Y
          );

        if (Math.abs(nextX) < 0.0005) {
          nextX = 0;
        }

        if (Math.abs(nextY) < 0.0005) {
          nextY = 0;
        }

        filtered.x = nextX;
        filtered.y = nextY;

        setDeviceMotionLook((previous) => {
          if (
            Math.abs(previous.x - nextX) <
              0.0005 &&
            Math.abs(previous.y - nextY) <
              0.0005
          ) {
            return previous;
          }

          return {
            x: nextX,
            y: nextY,
          };
        });
      }

      recenterFrame =
        requestAnimationFrame(
          recenterWhenStill
        );
    };

    recenterFrame =
      requestAnimationFrame(
        recenterWhenStill
      );

    window.addEventListener(
      "deviceorientation",
      onDeviceOrientation,
      true
    );

    window.addEventListener(
      "orientationchange",
      resetOrientationCenter
    );

    return () => {
      window.removeEventListener(
        "deviceorientation",
        onDeviceOrientation,
        true
      );

      cancelAnimationFrame(
        recenterFrame
      );

      window.removeEventListener(
        "orientationchange",
        resetOrientationCenter
      );
    };
  }, [
    deviceMotionEnabled,
    motionTrackingAllowed,
  ]);

  // =====================================================
  // AVA_IPAD_SINGLE_RESIZE_AUTHORITY_V4
  // IpadR3FSizeAuthority is the only iPad canvas sizing owner.
  // Avoid synthetic resize dispatches during landscape startup.
  // =====================================================

    // =====================================================
    // AVA_IPAD_FINAL_PORTRAIT_FRAME_LOCK_V9A6
    //
    // iPad portrait is the approved visual reference.
    //
    // IMPORTANT:
    // compactViewport previously applied a SECOND framing
    // transformation after the iPad viewport profile:
    //   scale * 1.18
    //   camera closer
    //   Y lower
    //   FOV narrower
    //
    // That caused the same iPad to render Ava differently
    // between portrait and landscape.
    //
    // iPad now bypasses all compactViewport framing changes.
    // Non-iPad behavior remains unchanged.
    // =====================================================
    const effectiveViewport =
      IOS_GPU_SAFE_MODE
        ? viewport
        : compactViewport
          ? {
              ...viewport,
              initialZ: viewport.initialZ - 0.18,
              farZ: viewport.farZ,
              nearZ: viewport.nearZ,
              modelScale: viewport.modelScale * 1.18,
              modelY: viewport.modelY - 0.06,
              fov: Math.max(34, viewport.fov - 3),
            }
          : viewport;

  // =====================================================
  // AVA GLOBAL EYE-LINE LIFT
  // Raises the complete avatar without altering geometry,
  // camera tracking, animation, responsive presets, or rig.
  // =====================================================
  // AVA_IPAD_EYE_LINE_LOCK_V9A6
  const AVA_EYE_LINE_LIFT =
    IOS_GPU_SAFE_MODE
      ? 0.16
      : compactViewport
        ? 0.13
        : 0.16;

  const { themeColors } = useContext(ThemeContext);




const hydratedRole =
    typeof window !== "undefined"
      ? String(
          localStorage.getItem("ava_user_role") ||
          localStorage.getItem("role") ||
          ""
        ).toLowerCase()
      : "";

  const hydratedSub =
    typeof window !== "undefined"
      ? String(
          localStorage.getItem("subscription_status") || ""
        ).toLowerCase()
      : "";

  const hydratedAdmin =
    ["admin", "owner", "superadmin"].includes(hydratedRole);

  const hydratedPro =
    hydratedAdmin ||
    hydratedRole === "vip" ||
    hydratedSub === "active";

  const avaEyeUnlocked =
    isPro ||
    isAdmin ||
    hydratedAdmin ||
    hydratedPro;


  const isLowPowerMode = !avaEyeUnlocked;

  const powerMultiplier =
    isLowPowerMode
      ? 0.35
      : 1.0;
  
  const [camEnabled, setCamEnabled] = useState<boolean>(false);
  const [effectiveEyeOpen, setEffectiveEyeOpen] = useState<boolean>(Boolean(avaEyeOpen) && avaEyeUnlocked);
  const [awakeLevel, setAwakeLevel] = useState<number>(0);
  const cachedAccentColor = useRef(
    new THREE.Color(getAvaAccentColor())
  );

  useEffect(() => {
    cachedAccentColor.current.set(getAvaAccentColor());
  }, [themeColors?.accent]);

  const lastInteractRef = useRef<number>(performance.now());

  const isDebugCam = () => {
    try {
      return localStorage.getItem("ava_cam_debug") === "1";
    } catch {
      return false;
    }
  };

  const readCamToggle = () => false;

  const writeCamToggle = (_open: boolean) => {
    try {} catch {}
  };

  useEffect(() => {
    setEffectiveEyeOpen(Boolean(avaEyeOpen) && avaEyeUnlocked);

  }, [avaEyeOpen, avaEyeUnlocked]);

  useEffect(() => {
    const onEyeToggle = (event: Event) => {
      const custom = event as CustomEvent<{ open?: boolean }>;
      const requestedOpen = !!custom?.detail?.open;
      const open = requestedOpen && avaEyeUnlocked;

      if (requestedOpen && !avaEyeUnlocked) {
        console.warn("[AvaFace] Ava Eye locked: paid plan or admin required.");
      }

      lastInteractRef.current = performance.now();
      setAwakeLevel(open ? (isLowPowerMode ? 0.35 : 1) : 0);
      setEffectiveEyeOpen(open);
      // Ava Vision camera must NEVER auto-enable.
// Ava Eye unlock is separate from camera runtime.
// Camera activates ONLY from explicit user action.
setCamEnabled(open && avaEyeUnlocked);
      writeCamToggle(open);

// Production debug removed
};

    window.addEventListener("ava-eye-toggle", onEyeToggle as EventListener);
    return () => {
      window.removeEventListener("ava-eye-toggle", onEyeToggle as EventListener);
    };
  }, [avaEyeUnlocked]);

  // Camera hotkey removed. Ava's Eye button is now the only camera control.

  const {
    pos: camRaw,
    faceDetected: camFaceDetected,
    cameraStatus: camCameraStatus,
    captureCanvasFrame,
  } = useCameraFaceTracking(camEnabled);

  const visionCaptureCanvasFrameRef =
    useRef<
      () => HTMLCanvasElement | null
    >(
      captureCanvasFrame
    );

  useEffect(() => {
    visionCaptureCanvasFrameRef.current =
      captureCanvasFrame;
  }, [captureCanvasFrame]);

  /*
   * Poll the existing Vision result cache for confirmed visible
   * objects, hands, gestures and readable text. This does not
   * create another camera stream or another upload loop.
   */
  const [
    camObjectDetected,
    setCamObjectDetected,
  ] = useState(false);

  useEffect(() => {
    if (!camEnabled) {
      setCamObjectDetected(false);
      return;
    }

    clearLatestVision();
    setCamObjectDetected(false);

    const syncDetectedObjects = () => {
      const vision =
        getLatestVision();

      const visibleObject =
        Boolean(
          vision &&
          (
            (
              Array.isArray(
                vision.objects
              ) &&
              vision.objects.length > 0
            ) ||
            (
              Array.isArray(
                vision.text
              ) &&
              vision.text.some(
                value =>
                  String(value || "")
                    .trim()
                    .length > 0
              )
            ) ||
            Number(
              vision.hands_detected || 0
            ) > 0 ||
            Boolean(
              vision.wave_detected
            ) ||
            String(
              vision.gesture_summary || ""
            ).trim().length > 0
          )
        );

      setCamObjectDetected(
        previous =>
          previous === visibleObject
            ? previous
            : visibleObject
      );
    };

    syncDetectedObjects();

    const detectionTimer =
      window.setInterval(
        syncDetectedObjects,
        350
      );

    return () => {
      window.clearInterval(
        detectionTimer
      );
    };
  }, [camEnabled]);

  const visionUserStatus:
    AvaVisionUserStatus =
      camCameraStatus === "issue"
        ? "issue"
        : camCameraStatus !== "live"
          ? "connecting"
          : (
              camFaceDetected ||
              camObjectDetected
            )
            ? "visible"
            : "searching";

  const latestVision = getLatestVision();
  const [mouseLook, setMouseLook] = useState({ x: 0, y: 0 });
    // =====================================================
    // AVA LIVE VISION
    // Uploads are now handled exclusively by:
    // src/hooks/useCameraFaceTracking.ts
    // This component only consumes vision results.
    // =====================================================



  useEffect(() => {
    if (camEnabled) return;

    const move = (x: number, y: number) => {
      const nx = THREE.MathUtils.clamp(
          -(x / window.innerWidth - 0.5) * 0.55,
          -0.35,
          0.35
        );
      // Softer vertical tracking for touch/mobile
      const rawNy = -(y / window.innerHeight - 0.5) * 2;

      // Prevent exaggerated upward gaze
      const ny = THREE.MathUtils.clamp(rawNy * 0.42, -0.38, 0.38);
      setMouseLook({ x: nx, y: ny });
    };

    const mouse = (e: MouseEvent) => move(e.clientX, e.clientY);
    const touch = (e: TouchEvent) => {
      const t = e.touches[0];
      if (t) move(t.clientX, t.clientY);
    };

    window.addEventListener("mousemove", mouse);
    window.addEventListener("touchmove", touch, { passive: true });

    return () => {
      window.removeEventListener("mousemove", mouse);
      window.removeEventListener("touchmove", touch);
    };
  }, [camEnabled]);

  const camLook = useMemo(() => {
    /*
     * Phase 4B: Vision tracking should follow the person
     * decisively rather than feeling delayed or restricted.
     */
    const sx = camRaw.x * 0.72;
    const sy = camRaw.y * 0.66;

    const X_OFFSET = 0.0;
    const Y_OFFSET = 0.02;

    const x = Math.max(-1, Math.min(1, (sx - X_OFFSET)));
    const y = Math.max(-1, Math.min(1, sy - Y_OFFSET));

    return { x, y };
  }, [camRaw.x, camRaw.y]);

  const look = useMemo(() => {
    const baseLook =
      camEnabled
        ? camLook
        : deviceMotionEnabled && motionTrackingAllowed
          ? deviceMotionLook
          : mouseLook;

    // =====================================================
    // AVA IOS STARTUP GAZE CORRECTION
    // The startup bias applies only to pointer/touch mode.
    // Camera and device-motion tracking remain unbiased.
    // =====================================================
    const startupVerticalBias =
      !camEnabled &&
      !(deviceMotionEnabled && motionTrackingAllowed) &&
      isIOSWebKit()
        ? 0.03
        : 0.0;

    return {
      x: baseLook.x,
      y: baseLook.y + startupVerticalBias,
    };
  }, [
    camEnabled,
    camLook,
    deviceMotionEnabled,
    deviceMotionLook,
    motionTrackingAllowed,
    mouseLook,
  ]);
  useEffect(() => {
    let raf = 0;

    const tick = () => {
      const now = performance.now();
      const dt = (now - lastInteractRef.current) / 1000;
      const base = camEnabled ? 1.0 : Math.max(0, 1 - dt / HALO_AWAKE_SECONDS);

      setAwakeLevel((prev) => prev + (base - prev) * 0.12);

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [camEnabled]);

  useEffect(() => {
    if (!camEnabled) return;
    if (!isDebugCam()) return;
// Production debug removed
}, [camEnabled, camRaw.x, camRaw.y, camLook.x, camLook.y]);

  // =============================================
  // 🔥 iPad: skip WebGL entirely to prevent GPU crash
  // =============================================


  // 🔥 FIX: No-op event system to prevent R3F addEventListener crash
  // R3F v9 createPointerEvents calls target.addEventListener on divRef.current
  // which can be undefined during iOS tab restoration or React double-mounting
  const noopEvents = (store: any) => ({
    priority: 1,
    enabled: false,
    compute: () => {},
    connected: undefined as Element | undefined,
    handlers: {} as Record<string, any>,
    update: () => {},
    connect: (target: Element) => {
      const { set } = store.getState();
      set((state: any) => ({
        events: { ...state.events, connected: target },
      }));
    },
    disconnect: () => {
      const { set } = store.getState();
      set((state: any) => ({
        events: { ...state.events, connected: undefined },
      }));
    },
  });
  return (
    <div
      className="ava-wrapper"
      data-ava-eye={
        effectiveEyeOpen ? "open" : "closed"
      }
    >
      <AvaRenderErrorBoundary
        resetKey={avaRenderKey}
        onError={() => {
          setAvaRenderStatus("error");
        }}
      >
        <Canvas
          key={avaRenderKey}
          frameloop="always"
        camera={{ position: [0, 0.24, effectiveViewport.initialZ], fov: IPAD_VISUAL_RESTRICTIONS
              ? Math.min(effectiveViewport.fov, 42)
              : effectiveViewport.fov }}
        gl={(defaults) => {
          const canvas = defaults.canvas as HTMLCanvasElement;
          // AVAAIO iPad GPU-safe renderer
          // Prefer the established WebGL2 path, with WebGL1 fallback,
          // while reducing framebuffer pressure on older iPads.
          if (IPAD_GPU_RESOURCE_SAFE) {
            const contextOptions = {
              alpha: true,
              antialias: true,
              preserveDrawingBuffer: false,
              powerPreference: "default" as WebGLPowerPreference,
              stencil: false,
              depth: true,
              failIfMajorPerformanceCaveat: false,
            };

            let context:
              | WebGL2RenderingContext
              | WebGLRenderingContext
              | null = null;

            try {
              context = canvas.getContext(
                "webgl2",
                contextOptions
              ) as WebGL2RenderingContext | null;

              if (context) {
                console.log(
                  "[AVA] iPad renderer: WebGL2 selected"
                );
              }
            } catch (error) {
              console.warn(
                "[AVA] iPad WebGL2 context failed",
                error
              );
            }

            if (!context) {
              try {
                context = canvas.getContext(
                  "webgl",
                  contextOptions
                ) as WebGLRenderingContext | null;

                if (context) {
                  console.log(
                    "[AVA] iPad renderer: WebGL1 fallback selected"
                  );
                }
              } catch (error) {
                console.warn(
                  "[AVA] iPad WebGL1 context failed",
                  error
                );
              }
            }

            canvas.addEventListener(
              "webglcontextlost",
              (event) => {
                console.warn(
                  "[AVA] WebGL context lost - preventing default"
                );
                event.preventDefault();
                setAvaRenderStatus("error");
              },
              false
            );

            canvas.addEventListener(
              "webglcontextrestored",
              () => {
                console.warn("[AVA] WebGL context restored");
                window.dispatchEvent(new Event("resize"));
              },
              false
            );

            if (!context) {
              console.warn(
                "[AVA] WebGL2 and WebGL1 unavailable; using Three.js automatic renderer selection"
              );

              return new THREE.WebGLRenderer({
                canvas,
                precision: "highp",
                powerPreference: "default",
                antialias: true,
                alpha: true,
                stencil: false,
                depth: true,
                failIfMajorPerformanceCaveat: false,
              });
            }

            return new THREE.WebGLRenderer({
              canvas,
              context,
              precision: "highp",
              powerPreference: "default",
              antialias: true,
              alpha: true,
              stencil: false,
              depth: true,
              logarithmicDepthBuffer: false,
            });
          }

          // Desktop / non-iPad: normal WebGL2 path
          return new THREE.WebGLRenderer({
            canvas,
            alpha: true,
            antialias: true,
            preserveDrawingBuffer: false,
            powerPreference: "high-performance",
            precision: "highp",
            stencil: false,
            depth: true,
            logarithmicDepthBuffer: false,
            failIfMajorPerformanceCaveat: false,
          });
        }}
        dpr={IPAD_GPU_RESOURCE_SAFE ? [1.25, 1.25] : [1, 2]}
        onCreated={({ gl, camera }) => {
          gl.setClearColor(0x000000, 0);
          gl.autoClear = true;

            // =========================================
            // WKWebView GPU stabilization
            // =========================================
            if (IPAD_VISUAL_RESTRICTIONS) {
              gl.physicallyCorrectLights = false;
            }

            // =========================================
            // iOS GPU stabilization
            // =========================================
            if (IPAD_GPU_RESOURCE_SAFE) {
              gl.shadowMap.enabled = false;
            }

            if (IPAD_VISUAL_RESTRICTIONS) {
              gl.toneMappingExposure = 0.88;
            }
          const cam = camera as THREE.PerspectiveCamera;
          cam.fov = effectiveViewport.fov;
          cam.updateProjectionMatrix();

          // =====================================================
          // AVA_IPAD_SINGLE_RESIZE_AUTHORITY_V4
          // Canvas onCreated intentionally does not resize the iPad
          // renderer. IpadR3FSizeAuthority owns wrapper/R3F sizing.
          // =====================================================

        }}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          background: "transparent",
          pointerEvents: "none",
          opacity:
            avaRenderStatus === "ready"
              ? 1
              : 0,
          transition:
            "opacity 260ms ease-out",
        }}
      >
        
                <AvaLighting
          ios={IOS_GPU_SAFE_MODE}
        />

        <IpadR3FSizeAuthority />

{/* AVA_IPAD_CAMERA_POSE_LOCK_V9A7
            iPad portrait is the visual master.
            Keep CameraRig centered in both orientations so
            device-orientation/look state cannot change Ava's
            apparent 3D pose after physical rotation. */}
<CameraRig
          look={
            IPAD_VISUAL_RESTRICTIONS
              ? { x: 0, y: 0 }
              : look
          }
          zoomTrigger={
            IPAD_VISUAL_RESTRICTIONS
              ? 0
              : zoomTrigger
          }
          farZ={effectiveViewport.farZ}
          nearZ={effectiveViewport.nearZ}
        />
            <Suspense
              fallback={
                <AvaBootSequence
                  onSlowLoad={() => {
                    setAvaRenderStatus((current) =>
                      current === "loading"
                        ? "slow"
                        : current
                    );
                  }}
                />
              }
            >
          {
            USE_IPAD_RAW_AVA_RENDERER
              ? (
                  /*
                   * AVA_IPAD_RAW_RENDER_V7
                   *
                   * Diagnostic:
                   * render the raw GLB scene only.
                   * Do not mount the full AvaModel runtime.
                   */
                  <IpadRawAvaRenderV7
                    scale={effectiveViewport.modelScale}
                    position={[
                      IOS_GPU_SAFE_MODE
                        ? 0
                        : compactViewport
                          ? -0.08
                          : 0,
                      effectiveViewport.modelY + AVA_EYE_LINE_LIFT,
                      effectiveViewport.modelZ
                    ]}
                    onReady={() => {
                      setAvaRenderStatus("ready");
                    }}
                    ready={avaRenderStatus === "ready"}
                    accentColor={
                      themeColors?.accent ||
                      getAvaAccentColor()
                    }
                  />
                )
              : (
                  <AvaModel
                    accentColor={themeColors?.accent || getAvaAccentColor()}
                    visionActive={camEnabled}
                    visionTrackingActive={camEnabled}
                    visionFaceDetected={camFaceDetected}
                    visionStatus={visionUserStatus}
                    visionCaptureCanvasFrameRef={visionCaptureCanvasFrameRef}
                    motionLookActive={
                      motionTrackingAllowed &&
                      deviceMotionEnabled &&
                      !camEnabled
                    }
                    speaking={speaking}
                    mouthLevel={mouthLevel}
                    expression={expression}
                    look={look}
                    awakeLevel={awakeLevel}
                    scale={effectiveViewport.modelScale}
                    position={[
                      IPAD_VISUAL_RESTRICTIONS
                        ? 0
                        : compactViewport
                          ? -0.08
                          : 0,
                      effectiveViewport.modelY + AVA_EYE_LINE_LIFT,
                      effectiveViewport.modelZ
                    ]}
                    avaEyeOpen={!!avaEyeOpen}
                    eyeOpen={effectiveEyeOpen}
                  />
                )
          }
            </Suspense>


        {
          USE_ENVIRONMENT && !IPAD_VISUAL_RESTRICTIONS
            ? <Environment preset="city" />
            : null
        }

        {
          USE_AVA_BLOOM_TEST && !IPAD_VISUAL_RESTRICTIONS
            ? (
                <EffectComposer multisampling={4}>
                  <Bloom
                    intensity={1.0}
                    luminanceThreshold={0.8}
                    luminanceSmoothing={0.5}
                    mipmapBlur
                  />
                </EffectComposer>
              )
            : null
        }
      </Canvas>
      </AvaRenderErrorBoundary>

      {
        avaRenderStatus !== "ready"
          ? (
              <div
                role="status"
                aria-live="polite"
                style={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 40,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "20px",
                  pointerEvents: "none",
                }}
              >
                <div
                  style={{
                    width: "min(92%, 370px)",
                    padding: "18px 20px",
                    borderRadius: "18px",
                    border:
                      "1px solid rgba(103, 232, 249, 0.34)",
                    background:
                      "rgba(2, 10, 20, 0.90)",
                    backdropFilter: "blur(14px)",
                    boxShadow:
                      "0 18px 55px rgba(0, 0, 0, 0.42)",
                    color: "white",
                    textAlign: "center",
                    pointerEvents: "auto",
                  }}
                >
                  <div
                    style={{
                      fontSize: "15px",
                      fontWeight: 700,
                      letterSpacing: "0.03em",
                    }}
                  >
                    {
                      avaRenderStatus === "loading"
                        ? "Loading Ava…"
                        : avaRenderStatus === "slow"
                          ? "Ava is taking longer than expected to appear."
                          : "Ava couldn’t finish loading."
                    }
                  </div>

                  <div
                    style={{
                      marginTop: "8px",
                      fontSize: "13px",
                      lineHeight: 1.55,
                      color:
                        "rgba(226, 232, 240, 0.84)",
                    }}
                  >
                    {
                      avaRenderStatus === "error"
                        ? (
                            <>
                              Please check your network connection and try again.
                              If the issue continues, switch to Local Mode from the dashboard.
                            </>
                          )
                        : "Please keep AVAAIO open while Ava finishes loading."
                    }
                  </div>

                  {
                    avaRenderStatus === "error"
                      ? (
                          <button
                            type="button"
                            onClick={retryAvaRenderer}
                            style={{
                              marginTop: "16px",
                              padding: "9px 16px",
                              borderRadius: "999px",
                              border:
                                "1px solid rgba(103, 232, 249, 0.68)",
                              background:
                                "rgba(8, 145, 178, 0.25)",
                              color: "white",
                              cursor: "pointer",
                              fontWeight: 600,
                            }}
                          >
                            Retry Ava
                          </button>
                        )
                      : null
                  }
                </div>
              </div>
            )
          : null
      }

      {camEnabled && (
        <AvaVisionLivePreview
          active={camEnabled}
          status={visionUserStatus}
          captureCanvasFrameRef={
            visionCaptureCanvasFrameRef
          }
        />
      )}

      {
          camEnabled &&
          Boolean(isAdmin) &&
          <AvaVisionHUD isAdmin />
        }

    </div>
  );
}

useGLTF.preload("/models/AVA_REVISED.glb");
